// London, "Fog on the Thames". An autumn night seen from the South Bank: the Houses of Parliament
// and Elizabeth Tower across the river, the London Eye turning exactly one capsule per loop,
// Westminster Bridge traffic, breathing fog banks, drizzle, plane-tree leaves and a Victorian pub.
import { W, H, LOOP, TAU, frac, clamp, smooth, wave, step, cycle, scroll,
  hash, rng, bayer, hex, makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, text, reflect, streak, makePitch, gradeAd, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const WL = 198;                                   // waterline under Parliament and the bridge
const EMB = 236;                                  // near embankment parapet top
const PAVE = 245;                                 // wet cobbled pavement
const BB = { cx: 206, base: 196, clockY: 104 };   // Elizabeth Tower (Big Ben)
const EYE = { cx: 330, cy: 121, r: 56 };          // London Eye
const RAIL = 173, DECK = 178;                     // Westminster Bridge parapet rail / deck edge
const PIERS = [214, 252, 290, 328, 366, 404, 442, 480];
const PUB_X = 390;                                // pub corner (building runs to the right edge)
const PHONE = { x: 347, y: 211, w: 15, base: 256 };
const LAMP = { x: 28, y: 139 };
const MOON = { x: 436, y: 28 };
const BONGS = [60, 63, 66, 69];                   // Big Ben strikes (loop seconds)
const FLOCK_T0 = 60, FLOCK_T1 = 114;              // pigeons scatter on the first bong, settle back
const BOAT = { t0: 118, t1: 178 };                // one tug passes per loop
const BUSES = [{ a: 24, b: 62, dir: 1 }, { a: 142, b: 180, dir: -1 }];
const CABS = [{ a: 92, b: 110, dir: -1 }, { a: 196, b: 213, dir: 1 }];
const BILL = { x: 224, y: 129, w: 42, h: 19 };    // poster face of the hoarding at the bridge's Westminster end, between Big Ben and the Eye
const BILL_LAMPS = [BILL.x + 6, BILL.x + 21, BILL.x + 35], BILL_LAMP_Y = BILL.y - 7;   // low gooseneck lamps over the top
// three hidden things (site/js/ui/secrets.js, exported as `secrets`): a fox sitting on the pub's roof, a swan on the
// Thames under the bridge's last arch, and a ghost that haunts the Victoria Tower's archway twice a loop
const FOX = { x: 413, y: 91 };                    // sprite origin: it sits on the cornice (row 98)
const SWAN = { x: 350, y: 197 };                  // sprite origin: its body rides the water just under the waterline
const GHOST = { x: 58, y: 180 };                  // sprite origin, in the dark of the Sovereign's Entrance (x 56..64, from row 177)
const GHOST_UP = [[84, 100], [204, 220]];         // it shows: 2 s fading in, hovering, 2 s fading out

/* ================= Sky: grey-violet overcast with sodium glow ================= */
const SKY = ['#110f18', '#15131e', '#1a1725', '#201c2d', '#272236', '#2f2940', '#383049', '#423851',
  '#4d4158', '#584a5e', '#645362', '#705c66', '#7c6668'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 205), 1.15) * 0.8;
    v += Math.max(0, 1 - Math.hypot((i - 235) / 330, (y - 205) / 125)) * 0.26;       // city glow
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 120, (y - MOON.y) / 75)) * 0.22;  // moonlit deck
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
function renderMoon() {
  const r = 7, s = r * 2 + 1; const [c, x] = makeCanvas(s, s);
  const craters = [[-2, -2, 1.5], [2, 1, 1.2], [-1, 3, 1], [3, -3, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    let col = dx + dy < -5 ? '#f4eef4' : '#dcd4e0';
    if (dd > r - 1.2) col = dx + dy > 0 ? '#aaa0b8' : '#c8c0d2';
    for (const [cx, cy, cr] of craters) if (Math.hypot(dx - cx, dy - cy) < cr) col = '#b8b0c4';
    px(x, i, y, col);
  }
  return c;
}

/* ================= Heavy low clouds (tileable, moonlit tops, sodium-lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 13, y: -6, h: 62, banks: 7, k: 1, pal: { top: '#342e3f', body: '#221e2c', core: '#1c1925', bot: '#3a3040', bot2: '#2c2635' } },
  { seed: 27, y: 30, h: 66, banks: 7, k: 2, pal: { top: '#443d4f', body: '#2b2636', core: '#241f2e', bot: '#58444f', bot2: '#3e3444' } },
  { seed: 41, y: 84, h: 62, banks: 6, k: 3, pal: { top: '#504759', body: '#332c3d', core: '#2b2535', bot: '#735a62', bot2: '#524350' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 60 + r() * 90, base = h * (0.5 + r() * 0.3);
    const np = 7 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 11 + r() * 19, ry = 4 + r() * 6.5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}

/* ================= Far city in the fog (blocks, two slim towers, a crane) ================= */
const CRANE = { x: 108, top: 104, jib0: 80, jib1: 152 };
function renderFar() {
  const [c, x] = makeCanvas(W, H);
  const r = rng(17), wins = [];
  const addWin = (wx, wy) => wins.push({ id: wins.length, x: wx, y: wy, n: 2 + Math.floor(r() * 7),
    p: 0.25 + r() * 0.35, off: r(), col: r() < 0.6 ? '#a88660' : '#8e7462' });
  // two slim modern towers far behind, softened by distance
  for (const [tx, tw, top] of [[252, 9, 124], [264, 7, 140], [150, 8, 132]]) {
    rect(x, tx, top, tw, WL - top, '#3b3447');
    rect(x, tx, top, 1, WL - top, '#4a4256');
    rect(x, tx + 1, top - 3, tw - 2, 3, '#342e40');
    rect(x, tx + (tw >> 1), top - 8, 1, 5, '#342e40');
    for (let wy = top + 2; wy < WL - 3; wy += 3) for (let wx = tx + 2; wx < tx + tw - 1; wx += 2) if (r() < 0.3) addWin(wx, wy);
  }
  // a construction crane (London always has one)
  const cm = '#3a3346', cl = '#4a4256';
  for (let y = CRANE.top; y < WL; y++) {
    rect(x, CRANE.x, y, 3, 1, (y & 1) ? cm : cl);
    if ((y & 3) === 0) px(x, CRANE.x + 1, y, '#2c2738');
  }
  rect(x, CRANE.jib0, CRANE.top, CRANE.jib1 - CRANE.jib0, 1, cl);
  rect(x, CRANE.jib0, CRANE.top + 2, CRANE.jib1 - CRANE.jib0, 1, cm);
  for (let i = CRANE.jib0; i < CRANE.jib1; i += 3) px(x, i + ((i >> 1) & 1), CRANE.top + 1, cm);
  rect(x, CRANE.jib0 + 2, CRANE.top + 3, 6, 4, '#2e2939');
  rect(x, CRANE.x - 1, CRANE.top - 8, 5, 8, cm); px(x, CRANE.x + 1, CRANE.top - 9, cl);
  for (let k = 0; k < 8; k++) { px(x, CRANE.x + 1 - k * 3, CRANE.top - 8 + k, '#3a3346'); px(x, CRANE.x + 3 + k * 5, CRANE.top - 8 + k, '#3a3346'); }
  rect(x, 134, CRANE.top + 3, 1, 30, '#2e2939'); rect(x, 132, CRANE.top + 33, 5, 2, '#2e2939');
  // low distant blocks along the far bank
  let bx = 0;
  while (bx < W) {
    const bw = 8 + Math.floor(r() * 18), top = 158 + Math.floor(r() * 24);
    rect(x, bx, top, bw, WL - top, r() < 0.5 ? '#3a3346' : '#353042');
    rect(x, bx, top, bw, 1, '#474053');
    if (r() < 0.35) rect(x, bx + 2 + Math.floor(r() * (bw - 4)), top - 3, 2, 3, '#353042');
    for (let wy = top + 3; wy < WL - 4; wy += 4) for (let wx = bx + 2; wx < bx + bw - 1; wx += 3) if (r() < 0.3) addWin(wx, wy);
    bx += bw;
  }
  return { c, wins };
}

/* A static violet haze that pushes the far city back (dithered, drawn with alpha) */
function renderHaze() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data, col = hex('#7d7490');
  for (let y = 90; y < WL; y++) for (let i = 0; i < W; i++) {
    const v = smooth(96, 186, y) * 0.9;
    if (v <= bayer(i, y) * 0.95) continue;
    const o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Houses of Parliament (floodlit gold Gothic river front) ================= */
const G = { hi: '#ffe6a6', l1: '#f0c46c', l2: '#d9a24e', m: '#bb863c', s1: '#96662e', s2: '#6a4424', d: '#40281a' };
const GLASS = '#2b2233', GLASS_T = '#44384a', WARM = '#fff2c4', WARM2 = '#ffcc6a';
const PX0 = 74, PX1 = 198, PTOP = 150, PBASE = 194;
const PAVIL = [[80, 91], [128, 139], [176, 187]];
const VT = { x0: 47, x1: 73, top: 74 };           // Victoria Tower: 98.5 m to Big Ben's 96 m, a 23 m square to its 12 m
const VT_CX = (VT.x0 + VT.x1) >> 1;               // its centre line: flagstaff, floodlight glow, streak on the river
const VT_WINS = [[7, 29], [37, 58], [66, 87]];    // its tall window stages (rows below VT.top) ...
const VT_ARC = [3, 33, 62, 90];                   // ... between bands of blind arcading
const CT = { cx: 122, top: 113 };
const TERRACE_LAMPS = [86, 110, 134, 158, 182];
function gothicWin(c, wx, wy, w, h, col) {        // pointed-arch window: glass with a pointed top row
  const mid = wx + (w >> 1);
  px(c, mid, wy, col); if (!(w & 1)) px(c, mid - 1, wy, col);
  rect(c, wx, wy + 1, w, h - 1, col);
}
function renderParliament(refl) {                 // refl: window-less copy used as the water's reflection source
  const [c, x] = makeCanvas(W, H);
  const r = rng(23), wins = [];
  const addWin = (wx, wy, w, h, lit) => {
    if (lit === 2) wins.push({ id: wins.length, x: wx, y: wy, w, h, n: 2 + Math.floor(r() * 6), p: 0.45 + r() * 0.3, off: r() });
    if (refl) { if (lit === 1) gothicWin(x, wx, wy, w, h, WARM); return; }
    gothicWin(x, wx, wy, w, h, lit === 1 ? WARM2 : GLASS);
    if (lit === 1) gothicWin(x, wx, wy, w, Math.max(2, h >> 1), WARM);
    else px(x, wx + (w >> 1), wy + 1, GLASS_T);
  };
  const pick = () => { const v = r(); return v < 0.3 ? 1 : v < 0.55 ? 2 : 0; };

  // --- Central Tower (octagonal lantern + spire), behind the roofs
  for (let y = CT.top; y < 143; y++) for (let dx = -5; dx <= 5; dx++) {
    let col = dx < -2 ? '#caa46a' : dx < 2 ? '#a88450' : '#80613e';
    if (dx === -5) col = '#dcb87a'; if (dx === 5) col = '#5c4530';
    if (!refl && y >= 119 && y <= 135 && (dx === -3 || dx === 0 || dx === 3) && !(y === 119 && dx !== 0)) col = '#3a2d32';
    if (y === 117 || y === 137) col = dx < 4 ? '#e6c688' : '#80613e';
    px(x, CT.cx + dx, y, col);
  }
  for (const dx of [-5, -2, 2, 5]) { rect(x, CT.cx + dx, 107, 1, 6, dx < 0 ? '#dcb87a' : '#80613e'); px(x, CT.cx + dx, 106, '#f0d49a'); }
  for (let y = 88; y < CT.top; y++) {
    const hw = Math.round((y - 88) / (CT.top - 88) * 5);
    for (let dx = -hw; dx <= hw; dx++) px(x, CT.cx + dx, y, dx < 0 ? '#c09a62' : dx === 0 ? '#dcb87a' : '#6e5436');
    if (y % 3 === 0 && hw > 0) { px(x, CT.cx - hw - 1, y, '#a68050'); px(x, CT.cx + hw + 1, y, '#5c4530'); }
  }
  px(x, CT.cx, 86, '#f0d49a'); px(x, CT.cx, 87, '#dcb87a');

  // --- steep slate roofs behind the parapet, with little flèches
  for (let y = 141; y < PTOP; y++) for (let i = PX0 + 2; i <= PX1; i++) {
    let col = ((i >> 1) + y) % 4 === 0 ? '#2f2a3a' : '#262130';
    if (y === 141) col = '#453d4e';
    px(x, i, y, col);
  }
  if (!refl) for (const fx of [100, 110, 152, 164]) {
    rect(x, fx, 131, 1, 10, G.s1); px(x, fx, 130, G.l2); px(x, fx, 129, G.hi);
    rect(x, fx - 1, 137, 3, 4, G.s2); px(x, fx - 1, 137, G.m);
  }

  // --- the long river facade: bays of 6 px, piers, tracery panels, two rows of Gothic windows
  for (let y = PTOP; y <= PBASE; y++) for (let i = PX0; i <= PX1; i++) {
    const m = (i - PX0) % 6, v = (y - PTOP) / (PBASE - PTOP);
    let col = v * 0.65 + 0.25 > bayer(i, y) ? G.l1 : G.l2;           // floodlit from below
    if (!refl) {                                                      // (the reflection copy skips thin verticals)
      if (m === 0) col = (y & 1) ? G.hi : G.l1;                       // buttress piers
      else if (m === 5) col = G.m;                                    // pier shadow
      else if (m === 3) col = G.l2;                                   // vertical tracery
    }
    if (y === PTOP) col = G.hi;
    else if (y === PTOP + 1) col = G.l1;
    else if (y === PTOP + 2) col = (m === 2 || m === 4) && !refl ? G.d : G.l1; // pierced parapet
    else if (y === PTOP + 3) col = G.s1;
    else if (y === 167 || y === 184) col = m === 5 ? G.l1 : G.hi;     // string courses
    else if (y === 168 || y === 185) col = G.s1;
    px(x, i, y, col);
  }
  if (!refl) for (let i = PX0; i <= PX1; i += 6) {                      // pinnacles on every pier
    rect(x, i, 144, 1, 6, G.l1); px(x, i, 143, G.hi);
    px(x, i - 1, 149, G.l2); px(x, i + 1, 149, G.m);
  }
  for (let bx = PX0; bx + 4 <= PX1; bx += 6) {
    addWin(bx + 2, 155, 3, 11, pick());
    addWin(bx + 2, 171, 3, 12, pick());
    addWin(bx + 2, 188, 3, 4, r() < 0.4 ? 1 : 0);
  }
  // pavilions: taller bays with pyramid roofs and corner pinnacles, projecting slightly
  for (const [a, b] of PAVIL) {
    for (let y = 142; y < PTOP + 4; y++) for (let i = a; i <= b; i++) {
      let col = (i - a) % 4 === 2 ? G.l2 : G.l1;
      if (y === 142) col = G.hi; else if (y === 143) col = G.m;
      px(x, i, y, col);
    }
    for (let k = 0; k < 6; k++) {
      const x0 = a + 1 + k, w = b - a - 1 - 2 * k;
      if (w < 1) break;
      rect(x, x0, 141 - k, w, 1, k === 5 ? G.l2 : '#342e3e');
      px(x, x0, 141 - k, '#4e4658');
    }
    if (!refl) {
      rect(x, (a + b) >> 1, 131, 1, 5, G.l2); px(x, (a + b) >> 1, 130, G.hi);
      for (const i of [a, b]) { rect(x, i, 132, 1, 10, i === a ? G.hi : G.l2); px(x, i, 131, G.hi); }
      rect(x, a, 144, 1, PBASE - 144, G.hi); rect(x, b + 1, 144, 1, PBASE - 144, G.s2);
    }
    addWin(a + 3, 145, 2, 5, r() < 0.5 ? 1 : 0); addWin(b - 4, 145, 2, 5, r() < 0.5 ? 1 : 0);
  }

  // --- Victoria Tower at the far end: massive square tower, corner turrets, tall traceried windows
  const VW = VT.x1 - VT.x0, VM = VW / 2;                                // dx of the far edge and of the centre line
  for (let y = VT.top; y <= PBASE; y++) for (let i = VT.x0; i <= VT.x1; i++) {
    const dx = i - VT.x0, v = (y - VT.top) / (PBASE - VT.top), r0 = y - VT.top;
    let col = v * 0.85 + 0.1 > bayer(i, y) ? G.l2 : G.m;               // floodlight fades with height
    if (refl) { if (dx > 5 && dx < VW - 5 && VT_WINS.some(([a, b]) => r0 > a && r0 < b)) col = G.s1; }
    else if (dx <= 3 || dx >= VW - 3) {                                // corner turrets run the full height
      col = dx === 0 ? G.l1 : dx === VW ? G.s2 : (dx === 3 || dx === VW - 3) ? G.s1 : (v * 0.8 + 0.2 > bayer(i, y) ? G.l1 : G.l2);
      if (r0 % 12 === 0) col = dx < VW - 3 ? G.hi : G.m;
    } else {
      for (const a of VT_ARC) {                                        // blind arcading: a lit course over little niches
        const k = r0 - a;
        if (k >= 0 && k <= 2 && dx >= 5 && dx <= VW - 5) col = k === 0 ? G.l1 : dx % 3 === 1 ? G.s2 : G.l2;
      }
      for (const [a, b] of VT_WINS) {                                  // tall windows: mullions, transoms, pointed heads
        if (r0 < a || r0 > b || dx < 6 || dx > VW - 6) continue;
        const k = r0 - a;
        if (k < 3 && Math.abs(dx - VM) > k * 2 + 1) continue;
        col = Math.abs(dx - VM) % 3 === 0 ? G.l1 : k % 9 === 8 ? G.l2 : (r0 > b - 8 ? '#5a3c2a' : '#3a2a30');
      }
      if (y >= 177 && Math.abs(dx - VM) <= 4) {                        // Sovereign's Entrance arch
        const k = y - 177;
        if (!(k < 3 && Math.abs(dx - VM) > k * 1.5 + 1)) col = y > 188 ? '#8a5a2a' : '#2a1c1a';
      }
    }
    px(x, i, y, col);
  }
  for (let i = VT.x0 + 4; i < VT.x1 - 3; i++) {                        // pierced top parapet
    px(x, i, VT.top - 4, G.hi);
    for (let y = VT.top - 3; y < VT.top; y++) px(x, i, y, ((i + y) & 1) ? G.l1 : G.d);
  }
  for (const i of [VT_CX - 5, VT_CX, VT_CX + 5]) { rect(x, i, VT.top - 8, 1, 4, G.l1); px(x, i, VT.top - 9, G.hi); }
  for (const t0 of [VT.x0, VT.x1 - 3]) {                              // turrets: open lantern stage, pinnacle caps
    for (let y = VT.top - 10; y < VT.top; y++) for (let k = 0; k < 4; k++) {
      let col = k === 0 ? G.l1 : k === 3 ? G.s1 : ((y & 1) ? G.l2 : G.l1);
      if (y >= VT.top - 8 && y <= VT.top - 6 && (k === 1 || k === 2)) col = G.s2;
      px(x, t0 + k, y, col);
    }
    rect(x, t0, VT.top - 11, 4, 1, G.hi);
    rect(x, t0 + 1, VT.top - 13, 2, 2, G.l2); rect(x, t0 + 1, VT.top - 17, 1, 4, G.l1); px(x, t0 + 1, VT.top - 18, G.hi);
  }
  if (!refl) rect(x, VT_CX, VT.top - 26, 1, 23, '#9a8260');                      // flagpole (flag drawn per frame)

  // --- the far-bank end: low Westminster buildings and autumn plane trees in Victoria Tower Gardens
  rect(x, 0, 172, VT.x0, PBASE - 172, '#4e3c34'); rect(x, 0, 172, VT.x0, 1, '#6e5644');
  for (let wy = 176; wy < 190; wy += 5) for (let wx = 2; wx < VT.x0 - 2; wx += 4) if (r() < 0.45) rect(x, wx, wy, 2, 2, r() < 0.5 ? '#e0a860' : '#b88450');
  for (const tx of [9, 25, 41]) {
    const cy = 180 + Math.floor(r() * 3), rx = 8 + r() * 2, ry = 7 + r() * 2;
    rect(x, tx, cy + 4, 2, PBASE - cy - 4, '#20161a');
    for (let y = Math.floor(cy - ry - 2); y <= cy + ry; y++) for (let i = Math.floor(tx - rx - 3); i <= tx + rx + 3; i++) {
      const dx = (i - tx) / rx, dy = (y - cy) / ry;
      let v = 1 - dx * dx - dy * dy + (bayer(i, y) - 0.5) * 0.4 + (r() - 0.5) * 0.2;
      if (v <= 0) continue;
      let col = v > 0.45 ? '#2a1c1e' : '#3a2620';
      if (y > cy + 1 && r() < 0.5) col = y > cy + ry * 0.5 ? '#b0602a' : '#7a3e22';   // leaves lit from the terrace
      else if (y < cy - ry * 0.4 && v < 0.35) col = '#5a4048';
      px(x, i, y, col);
    }
  }
  // river terrace wall along Parliament
  for (let i = 0; i < 216; i++) {
    px(x, i, PBASE, '#c8a468'); px(x, i, PBASE + 1, (i % 9 === 0) ? '#6a5036' : '#8a6a44');
    px(x, i, PBASE + 2, '#5a4430'); px(x, i, PBASE + 3, '#3a2c26');
  }
  if (!refl) for (const lx of TERRACE_LAMPS) { rect(x, lx, PBASE - 3, 1, 3, '#3a2a20'); px(x, lx, PBASE - 4, '#fff0c0'); }

  renderElizabeth(x);
  return { c, wins };
}

/* ================= Elizabeth Tower: shaft, glowing clock, belfry, spire, Ayrton light ================= */
function renderElizabeth(x) {                    // real proportions ~7.9:1: shaft 17 px, clock stage 19 px
  const cx = BB.cx;
  for (let y = 114; y <= BB.base; y++) for (let dx = -8; dx <= 8; dx++) {         // shaft
    const i = cx + dx, v = (y - 114) / (BB.base - 114);
    let c = v * 0.55 + 0.35 > bayer(i, y) ? G.l1 : G.l2;
    if (dx === -8) c = G.hi; else if (dx === -7) c = G.l1;
    else if (dx === 7) c = G.s1; else if (dx === 8) c = G.s2;
    else if (dx === -5 || dx === -2 || dx === 2 || dx === 5) c = G.m;
    if ((y - 114) % 16 === 2) c = dx < 7 ? G.hi : G.m;
    else if ((y - 114) % 16 === 3) c = dx < 7 ? G.m : G.s2;
    px(x, i, y, c);
  }
  for (const wy of [120, 136, 152, 168]) for (const dx of [-3, 0, 3]) {
    rect(x, cx + dx, wy, 1, 7, '#3e2818'); px(x, cx + dx, wy + 6, G.s1);
  }
  for (let y = 186; y <= BB.base; y++) for (let dx = -9; dx <= 9; dx++)            // plinth
    px(x, cx + dx, y, y === 186 ? G.hi : dx < 7 ? ((dx + y) % 4 === 0 ? G.s1 : G.m) : G.s2);
  for (let y = 95; y <= 113; y++) for (let dx = -9; dx <= 9; dx++) {               // clock stage
    let c = G.l1;
    if ((Math.abs(dx) >= 6 && (y <= 98 || y >= 110)) && ((dx + y) & 1)) c = G.hi;   // gilded corners
    else if (Math.abs(dx) === 7 && y > 98 && y < 110) c = G.l2;                     // the dial's square frame
    if (dx === -9) c = G.hi; else if (dx >= 8) c = dx === 9 ? G.s2 : G.s1;
    if (y === 95 || y === 113) c = dx < 8 ? G.hi : G.m;
    else if (y === 96 || y === 112) c = G.m;
    px(x, cx + dx, y, c);
  }
  drawDial(x, cx, BB.clockY, false);
  for (let y = 90; y <= 94; y++) for (let dx = -9; dx <= 9; dx++)                  // pierced gallery
    px(x, cx + dx, y, y === 90 ? G.hi : y === 94 ? G.m : (dx & 1) ? G.l1 : (y === 91 ? G.s1 : G.d));
  for (const dx of [-9, 9]) { rect(x, cx + dx, 84, 1, 6, dx < 0 ? G.hi : G.l2); px(x, cx + dx, 83, G.hi); }
  for (let y = 77; y <= 89; y++) for (let dx = -8; dx <= 8; dx++) {                // belfry
    let c = dx < 6 ? G.l1 : G.s1;
    if (dx === -8) c = G.hi; else if (dx === 8) c = G.s2;
    const oc = dx <= -3 ? -4 : dx <= 1 ? 0 : 4;
    if (Math.abs(dx - oc) <= 1 && y >= 79 && y <= 88 && !(y === 79 && dx !== oc)) c = y > 85 ? '#6a3c1a' : '#2a1810';
    if (y === 89) c = G.hi;
    px(x, cx + dx, y, c);
  }
  for (let y = 74; y <= 76; y++) rect(x, cx - 9, y, 19, 1, y === 74 ? G.hi : y === 75 ? G.l1 : G.s1);
  for (const dx of [-9, -3, 3, 9]) { rect(x, cx + dx, 68, 1, 6, dx < 0 ? G.l1 : G.l2); px(x, cx + dx, 67, G.hi); }
  for (let y = 56; y <= 73; y++) {                                                  // spire roof
    const hw = Math.round(2.5 + (y - 56) / 17 * 5.5);
    for (let dx = -hw; dx <= hw; dx++) {
      let c = dx < 0 ? '#3c3446' : '#241f2c';
      if (dx === 0) c = G.l2; else if (dx === -hw) c = G.l1; else if (dx === hw) c = G.s1;
      if (y === 61 || y === 67) c = dx < hw ? G.l2 : G.s1;
      px(x, cx + dx, y, c);
    }
  }
  for (let y = 50; y <= 55; y++) for (let dx = -2; dx <= 2; dx++)                  // Ayrton light lantern
    px(x, cx + dx, y, (y === 50 || y === 55) ? G.l1 : Math.abs(dx) === 2 ? G.l2 : '#fff2c0');
  for (let y = 40; y < 50; y++) { px(x, cx, y, G.l1); if (y >= 45) { px(x, cx - 1, y, G.hi); px(x, cx + 1, y, G.s1); } }
  for (let y = 34; y < 40; y++) px(x, cx, y, G.hi);
  px(x, cx - 1, 36, G.l1); px(x, cx + 1, 36, G.l1);
}
/* the dial: dark iron ring, opal face, hour marks; the hands show London's time, drawn per frame (drawParliament) */
function drawDial(x, cx, cy, lit) {
  for (let y = -7; y <= 7; y++) for (let i = -7; i <= 7; i++) {
    const dd = Math.hypot(i, y);
    if (dd > 6.6) continue;
    px(x, cx + i, cy + y, dd > 5.6 ? '#2a1a10' : lit ? '#fffdf2' : (i + y < 0 ? '#fbf1d4' : '#f0e2bc'));
  }
  for (let h = 0; h < 12; h++) {
    const a = h / 12 * TAU;
    px(x, Math.round(cx + Math.sin(a) * 4.6), Math.round(cy - Math.cos(a) * 4.6), h % 3 === 0 ? '#3a2614' : '#9a8460');
  }
}

/* ================= London Eye =================
   The wheel turns exactly one capsule spacing (1/32 turn) per loop, so t = 240 maps it onto itself.
   Rim, truss and spokes have 32-fold symmetry and are pre-rendered in EYE_NF rotation steps;
   capsules are placed per frame at their exact angle. */
const EYE_NF = 60, CAPS = 32, CAP_STEP = TAU / CAPS;
function renderEyeFrames() {
  const R = EYE.r, o = R + 5, S = o * 2 + 1, n = S * S;
  const dist = new Float32Array(n), ang = new Float32Array(n);
  for (let y = 0; y < S; y++) for (let i = 0; i < S; i++) {
    const dx = i - o, dy = y - o; dist[y * S + i] = Math.hypot(dx, dy); ang[y * S + i] = Math.atan2(dy, dx);
  }
  const C = { rim: hex('#ff6f8c'), rimHi: hex('#ffb4c2'), rim2: hex('#b84468'), truss: hex('#7a3a5c'),
    spoke: hex('#5a4a6c'), hub: hex('#f0e4f4'), hubD: hex('#8a7a9e'), collar: hex('#6e5a82') };
  const frames = [];
  for (let f = 0; f < EYE_NF; f++) {
    const rot = f / EYE_NF * CAP_STEP;
    const [c, x] = makeCanvas(S, S);
    const img = x.createImageData(S, S), d = img.data;
    for (let p = 0; p < n; p++) {
      const dd = dist[p];
      if (dd > R + 0.6) continue;
      const a = (ang[p] - rot) / CAP_STEP;
      let col = null;
      if (dd > R - 0.6) col = (ang[p] < -0.5 && ang[p] > -2.64) ? C.rimHi : C.rim;
      else if (dd > R - 3.6 && dd <= R - 2.6) col = C.rim2;
      else if (dd > R - 2.6) { if (frac(a * 3) < 0.34) col = C.truss; }
      else if (dd < 2.2) col = C.hub;
      else if (dd < 3.6) col = C.hubD;
      else if (dd > 7.5 && dd < 8.5) col = C.collar;
      else if (dd > 8.5 && Math.abs(a - Math.round(a)) * CAP_STEP * dd < 0.5) col = C.spoke;
      if (!col) continue;
      const q = p * 4; d[q] = col[0]; d[q + 1] = col[1]; d[q + 2] = col[2]; d[q + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    frames.push(c);
  }
  return { frames, o };
}
/* additive dithered glow around a ring of radius R (the lit rim in the fog) */
function makeRingHalo(R, width, col, maxA) {
  const r = R + width, s = r * 2 + 1, rgb = hex(col);
  const [c, x] = makeCanvas(s, s);
  const img = x.createImageData(s, s), d = img.data;
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const g = 1 - Math.abs(Math.hypot(i - r, y - r) - R) / width;
    if (g <= 0) continue;
    const q = Math.floor(g * g * 6 + bayer(i, y)) / 6;
    if (q <= 0) continue;
    const o = (y * s + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(Math.min(1, q) * maxA * 255);
  }
  x.putImageData(img, 0, 0);
  return { c, r };
}
function renderCapsule() {
  const [c, x] = makeCanvas(5, 3);
  rect(x, 1, 0, 3, 1, '#f4f6ff');
  px(x, 0, 1, '#6e7298'); rect(x, 1, 1, 3, 1, '#d2daf6'); px(x, 4, 1, '#6e7298');
  rect(x, 1, 2, 3, 1, '#8890b8');
  return c;
}

/* ================= South Bank behind the bridge: County Hall and the Eye's A-frame ================= */
function renderSouthBank() {
  const [c, x] = makeCanvas(W, H);
  const r = rng(29), wins = [];
  const X0 = 344;
  for (let y = 142; y < 148; y++) {                                   // slate roof with dormers
    const inset = 148 - y;
    rect(x, X0 + inset, y, W - X0 - inset, 1, y === 142 ? '#4a4254' : '#2e2838');
  }
  for (let i = X0 + 8; i < W; i += 9) { rect(x, i, 140, 3, 4, '#3a3446'); px(x, i + 1, 142, '#b89060'); }
  for (let y = 148; y < DECK; y++) for (let i = X0; i < W; i++) {
    let col = (i - X0) % 7 === 0 ? '#352d3c' : '#40364a';
    if (y === 148) col = '#5e5468'; else if (y === 149) col = '#2c2634';
    px(x, i, y, col);
  }
  for (let wy = 152; wy < DECK - 2; wy += 5) for (let wx = X0 + 2; wx < W - 1; wx += 3) {
    if ((wx - X0) % 7 === 0) continue;
    const v = r();
    if (v < 0.22) wins.push({ id: wins.length, x: wx, y: wy, n: 2 + Math.floor(r() * 5), p: 0.5, off: r() });
    rect(x, wx, wy, 1, 2, v < 0.12 ? '#c89658' : '#262030');
  }
  // A-frame legs from the hub down to the South Bank (behind the wheel): white tubes, their left edge lit by the hub
  // and falling off, dithered, into the fog towards their feet
  for (const bx of [296, 309]) {
    const n = Math.round(Math.hypot(bx - EYE.cx, DECK - EYE.cy));
    for (let k = 0; k <= n; k++) {
      const xx = Math.round(EYE.cx + (bx - EYE.cx) * k / n), yy = Math.round(EYE.cy + (DECK - EYE.cy) * k / n);
      px(x, xx, yy, bayer(xx, yy) < k / n * 0.8 ? '#7a6e90' : '#968aac'); px(x, xx + 1, yy, '#62566e');
    }
  }
  return { c, wins };
}

/* ================= Westminster Bridge: green iron arches, stone piers, three-globe lamps ================= */
const BR_LAMPS = PIERS.slice(0, -1).map(p => p + 1);
function renderBridge() {
  const [c, x] = makeCanvas(W, H);            // body (behind the traffic)
  const [c2, x2] = makeCanvas(W, H);          // parapet + lamps (in front of the traffic)
  for (let i = 212; i < W; i++) {
    px(x, i, DECK, '#6aa487'); px(x, i, DECK + 1, '#3c6e58');
    px(x, i, DECK + 2, (i % 4 === 0) ? '#8ac2a4' : '#2f5a48');
    px(x, i, DECK + 3, '#2a5040'); px(x, i, DECK + 4, '#1a3228');
  }
  for (let k = 0; k < PIERS.length - 1; k++) {
    const a = PIERS[k] + 4, b = PIERS[k + 1] - 4, mid = (a + b) / 2, half = (b - a) / 2 + 1;
    for (let i = a; i <= b; i++) {
      const u = (i - mid) / half, top = Math.round(WL - 1 - 13 * Math.sqrt(Math.max(0, 1 - u * u)));
      for (let y = DECK + 5; y < WL; y++) {
        let col;
        if (y < top - 1) col = ((i - a) % 6 === 3 && (y - DECK) % 4 === 2) ? '#16281f' : '#244536';
        else if (y === top - 1) col = '#7ab89a';
        else if (y === top) col = '#3a6a56';
        else col = '#100e1a';
        px(x, i, y, col);
      }
    }
  }
  for (const p of PIERS) for (let y = DECK + 5; y <= WL; y++) for (let i = p - 3; i <= p + 3; i++) {
    let col = i === p - 3 ? '#9a94a6' : i >= p + 2 ? '#3a3646' : ((y & 3) === 0 ? '#4c4858' : '#5c5768');
    if (y === DECK + 5) col = '#aaa4b4';
    px(x, i, y, col);
  }
  for (let i = 210; i < W; i++) {                                     // open Gothic balustrade
    px(x2, i, RAIL, '#86c2a4'); px(x2, i, RAIL + 1, '#3e7060');
    const m = i % 4;
    if (m === 0) for (let y = RAIL + 2; y < DECK - 1; y++) px(x2, i, y, '#2e5a48');
    else if (m === 2) px(x2, i, RAIL + 3, '#2e5a48');
    px(x2, i, DECK - 1, '#2a4c3e');
  }
  for (const lx of BR_LAMPS) {
    rect(x2, lx, 161, 1, RAIL - 161, '#1c3228'); px(x2, lx, 168, '#3e6e58');
    rect(x2, lx - 2, 164, 5, 1, '#1c3228');
    for (const [gx, gy] of [[lx - 2, 162], [lx + 2, 162], [lx, 159]]) {
      px(x2, gx, gy - 1, '#1c3228'); rect(x2, gx, gy, 1, 2, '#fff0c8'); px(x2, gx, gy + 1, '#ffc870');
    }
  }
  return { body: c, front: c2 };
}

/* ================= The Thames ================= */
function renderWater() {
  const h = EMB - WL; const [c, x] = makeCanvas(W, h);
  const P = ['#1b192e', '#141326', '#0f0e1f', '#0b0a19'].map(hex);
  const img = x.createImageData(W, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < W; i++) {
    const v = y / h * (P.length - 1), k = Math.floor(v);
    const col = P[Math.min(P.length - 1, k + (v - k > bayer(i, y) ? 1 : 0))], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
/* a Thames tug: black hull, red boot-top, white wheelhouse, red funnel, masthead light */
function renderBoat() {
  const w = 42, h = 15; const [c, x] = makeCanvas(w, h);
  rect(x, 29, 0, 1, 5, '#2a2632'); px(x, 29, 0, '#ffffff');                 // mast + masthead light
  rect(x, 13, 2, 4, 6, '#b8382e'); rect(x, 13, 2, 4, 1, '#1a1418'); rect(x, 13, 3, 1, 5, '#e05a48');
  rect(x, 20, 3, 12, 6, '#cfcad6'); rect(x, 20, 3, 12, 1, '#f0ecf4'); rect(x, 31, 4, 1, 5, '#8e88a0');
  for (let i = 21; i < 31; i += 3) rect(x, i, 5, 2, 2, '#ffd27a');              // lit wheelhouse windows
  px(x, 22, 5, '#fff0c0');
  rect(x, 6, 7, 30, 2, '#3a3440'); rect(x, 6, 7, 30, 1, '#6a6272');            // deck + rail
  for (let i = 7; i < 35; i += 3) px(x, i, 6, '#6a6272');
  for (let y = 9; y < h; y++) {                                                // hull with a raked bow
    const x0 = 2 + Math.max(0, y - 12), x1 = 40 - Math.max(0, (y - 9) * 2);
    rect(x, x0, y, x1 - x0 + 1, 1, y === 9 ? '#4a4452' : y >= 13 ? '#6a2424' : '#15131b');
  }
  rect(x, 3, 11, 36, 1, '#26222c');
  for (const tx of [8, 18, 28]) rect(x, tx, 10, 2, 2, '#3a2a22');               // fender tyres
  px(x, 20, 8, '#ff4a4a'); px(x, 3, 9, '#fff4d8');                              // side + stern lights
  return c;
}
function flipH(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
/* red double-decker, facing right: lit windows on both decks, passenger silhouettes, blind, lamps */
const BUS_W = 25, BUS_H = 14;
function renderBus(seed) {
  const [c, x] = makeCanvas(BUS_W, BUS_H); const r = rng(seed);
  const R0 = '#d62a2c', RH = '#f25a4e', RD = '#8e1a22', WIN = '#ffd98c', WINH = '#ffeebc', HEAD = '#3a2216';
  rect(x, 1, 0, BUS_W - 2, 1, RH);
  rect(x, 0, 1, BUS_W, 11, R0);
  rect(x, 0, 1, 1, 11, RH);
  for (const [y0, y1] of [[2, 4], [7, 9]]) {
    for (let wx = 2; wx < BUS_W - 3; wx += 4) {
      rect(x, wx, y0, 3, y1 - y0 + 1, WIN); rect(x, wx, y0, 3, 1, WINH);
      if (r() < 0.55) { const hx = wx + Math.floor(r() * 3); px(x, hx, y1 - 1, HEAD); px(x, hx, y1, HEAD); if (r() < 0.5) px(x, hx + (hx < wx + 2 ? 1 : -1), y1, HEAD); }
    }
  }
  rect(x, BUS_W - 3, 2, 2, 2, '#fff4c0'); px(x, BUS_W - 3, 3, '#ffb040');         // destination blind
  rect(x, BUS_W - 3, 7, 2, 3, '#9ab0c8'); px(x, BUS_W - 2, 7, '#d8e4f0');         // windscreen
  rect(x, 0, 5, BUS_W, 1, RD); rect(x, 1, 6, BUS_W - 2, 1, '#b82228');
  rect(x, 0, 10, BUS_W, 2, RD);
  px(x, BUS_W - 1, 10, '#fff8e0'); px(x, 0, 10, '#ff3a30');                       // head + tail lamps
  for (const wx of [3, 18]) { rect(x, wx, 11, 4, 3, '#121016'); rect(x, wx + 1, 12, 2, 1, '#5a5662'); }
  return c;
}
/* black cab, facing right, with its amber roof sign lit */
const CAB_W = 15, CAB_H = 9;
function renderCab() {
  const [c, x] = makeCanvas(CAB_W, CAB_H);
  const K = '#131218', KH = '#4c4a5a', WIN = '#b89468';
  rect(x, 6, 0, 3, 1, '#ffb040');                                                 // roof sign
  rect(x, 4, 1, 7, 1, KH);
  rect(x, 3, 2, 9, 2, K); rect(x, 4, 2, 3, 2, WIN); rect(x, 8, 2, 3, 2, WIN); px(x, 11, 2, '#6a7a8a');
  rect(x, 0, 4, CAB_W, 3, K); rect(x, 0, 4, CAB_W, 1, KH);
  px(x, CAB_W - 1, 5, '#fff8e0'); px(x, 0, 5, '#ff3a30');
  for (const wx of [2, 10]) { rect(x, wx, 7, 3, 2, '#0a090e'); px(x, wx + 1, 7, '#4a4652'); }
  return c;
}

/* ================= Foreground: embankment parapet and wet cobbles ================= */
const WARM_SPOTS = [                                   // light pools baked into the wet stone sheen
  { x: LAMP.x, y: 262, r: 46, col: '#7a5a3e' }, { x: PHONE.x + 7, y: 258, r: 22, col: '#6e2c30' },
  { x: 414, y: 262, r: 34, col: '#7a5236' }, { x: 463, y: 262, r: 24, col: '#7a5236' },
];
const BENCH = { x: 150, y: 235 };
function renderGround() {
  const [c, x] = makeCanvas(W, H);
  const r = rng(31);
  // granite parapet of the embankment
  for (let i = 0; i < W; i++) {
    px(x, i, EMB, '#7a7288'); px(x, i, EMB + 1, '#5a5468'); px(x, i, EMB + 2, '#3c3748');
    for (let y = EMB + 3; y < PAVE; y++) {
      const blk = ((i + (((y - EMB - 3) >> 2) & 1) * 9) % 18 === 0) || y === EMB + 6;
      px(x, i, y, blk ? '#1e1b27' : (y === EMB + 3 ? '#2a2634' : '#312c3c'));
    }
  }
  for (let i = 0; i < W; i++) if (r() < 0.08) px(x, i, EMB, '#a49cb4');
  // cobbles: rows grow toward the viewer, each stone with a wet top sheen
  const rows = [[PAVE, 2], [PAVE + 2, 3], [PAVE + 5, 3], [PAVE + 8, 4], [PAVE + 12, 4], [PAVE + 16, 5], [PAVE + 21, 5]];
  rect(x, 0, PAVE, W, H - PAVE, '#121019');
  rows.forEach(([y0, rh], ri) => {
    let cx = -Math.floor(r() * 6);
    const sw = 4 + ri;
    while (cx < W) {
      const w = sw + Math.floor(r() * 3);
      for (let y = y0; y < y0 + rh - (rh > 2 ? 1 : 0); y++) for (let i = cx + 1; i < cx + w; i++) {
        if (i < 0 || i >= W || y >= H) continue;
        const edge = (i === cx + 1 || i === cx + w - 1) && (y === y0 || y === y0 + rh - 2);
        if (edge && rh > 2) continue;                                   // rounded corners
        let col = ['#26232f', '#2a2634', '#221f2b'][Math.floor(hash(i >> 2, y0, 7) * 3)];
        if (y === y0) col = '#3a3648';
        for (const s of WARM_SPOTS) {
          const dd = Math.hypot((i - s.x) / s.r, (y - s.y) / (s.r * 0.35));
          if (dd < 1 && y <= y0 + 1 && bayer(i, y) < (1 - dd) * 1.3) col = s.col;
        }
        px(x, i, y, col);
      }
      cx += w;
    }
  });
  // a cast-iron Embankment bench against the parapet
  const BX = BENCH.x, I = '#15141c', IH = '#3a3646';
  for (const [y, col] of [[BENCH.y, '#7a5634'], [BENCH.y + 2, '#5e4028'], [BENCH.y + 4, '#5e4028']]) {
    rect(x, BX + 2, y, 26, 1, col); rect(x, BX + 2, y + 1, 26, 1, '#2e1e14');
  }
  rect(x, BX + 1, BENCH.y + 8, 28, 1, '#8a6440'); rect(x, BX + 1, BENCH.y + 9, 28, 1, '#4a3020');
  for (const ex of [BX, BX + 27]) {
    rect(x, ex + 1, BENCH.y - 1, 1, 10, I); px(x, ex + 1, BENCH.y - 1, IH);
    rect(x, ex, BENCH.y + 6, 4, 1, I); px(x, ex, BENCH.y + 6, IH);
    rect(x, ex + 2, BENCH.y + 10, 1, 6, I); rect(x, ex, BENCH.y + 10, 1, 6, I); px(x, ex + 1, BENCH.y + 12, I);
    rect(x, ex - 1, BENCH.y + 15, 5, 1, I);
  }
  rect(x, BX + 14, BENCH.y + 10, 1, 5, I);
  return c;
}

/* ================= Victorian cast-iron lamppost (left): an Embankment "dolphin" lamp ================= */
// Its base: two cast-iron sturgeons (Vulliamy's "dolphins") twined round the post, tails up under a collar and heads
// down on the pedestal. Each body is a tapering curve from head to tail; the left fish crosses in front of the right.
const DOLPHIN_Y = 236, DOLPHIN_H = 14, DOLPHIN_W = 19;              // local grid: columns LAMP.x - 9 .. LAMP.x + 9
const DCOL = { k: '#15141c', m: '#2e2632', h: '#3a3646', w: '#8a6440', W: '#e0b07c', d: '#07060a' };
const D_BODY = [null, null, null, [7, 11], [6, 12], [5, 13], [5, 13], [4, 14], [4, 14], [4, 14], [5, 13], [5, 13], [6, 12], [7, 11]];
const D_TAIL = [[6, 2, 'k'], [5, 2, 'k'], [4, 1, 'k'], [5, 1, 'h'], [3, 0, 'k'], [5, 0, 'k'], [2, -1, 'w'], [5, -1, 'w']];   // left; mirrored
function renderDolphins(x, X) {
  const w = DOLPHIN_W, h = DOLPHIN_H, m = w >> 1;
  const head = (i, j, s) => ((i + 0.5 - (m + 0.5 + 6 * s)) / 3.4) ** 2 + ((j + 0.5 - 10.8) / 2.5) ** 2 < 1;
  const inA = (i, j) => { const xl = Math.round(10 - 1.2 * (j - 3)); return j >= 3 && j <= 9 && i >= xl && i <= xl + 3; };
  const inB = (i, j) => { const r = D_BODY[j]; return !!r && i >= r[0] && i <= r[1]; };
  const inS = (i, j) => inB(i, j) || head(i, j, -1) || head(i, j, 1);
  for (let j = 3; j < h; j++) for (let i = 0; i < w; i++) {
    if (!inS(i, j)) continue;
    let col = 'k';
    const hs = head(i, j, -1) ? -1 : head(i, j, 1) ? 1 : 0;
    if (hs) {                                                        // the heads, bulging out over the pedestal
      if (!head(i, j - 1, hs)) col = 'w';
      else if (!head(i - hs, j, hs)) col = 'd';                      // gill line, where the body starts
      else if (!head(i + hs, j, hs)) col = hs < 0 ? 'h' : 'm';
      else if (!head(i, j - 2, hs)) col = 'm';
    } else if (inA(i, j)) {                                          // the front fish: tail top right, head bottom left
      if (!inA(i, j - 1)) col = 'w';                                 // its back, lit from the lantern above
      else if (!inA(i, j + 1)) col = 'd';                            // its belly over the other fish
      else if (!inA(i, j - 2)) col = 'm';
    } else {                                                         // the other fish, round the back of the post
      if (!inS(i, j - 1)) col = 'w';
      else if (inA(i, j - 1)) col = 'k';                             // in the front fish's shadow
      else if (!inS(i, j - 2) || !inS(i + 1, j)) col = 'm';
      else if (!inS(i - 1, j)) col = 'h';
      else if ((i + 2 * j) % 5 === 0) col = 'm';                     // cast scales
    }
    px(x, X - m + i, DOLPHIN_Y + j, DCOL[col]);
  }
  for (const s of [-1, 1]) {
    const o = (i, j, c) => px(x, X + s * (m - i), DOLPHIN_Y + j, DCOL[c]);   // i counted from the outer edge
    o(2, 10, 'W'); o(3, 10, 'd');                                    // eye
    o(0, 12, 'k'); o(0, 13, 'h'); o(1, 13, 'd'); o(2, 13, 'k');      // snout down on the pedestal, mouth open
    o(6, 13, 'h'); o(7, 13, 'k');                                    // pectoral fin
    for (const [i, j, c] of D_TAIL) o(i, j, c);                      // forked tail flicking up and out
  }
}
function renderLamp() {
  const [c, x] = makeCanvas(W, H);
  const X = LAMP.x, I = '#15141c', IH = '#34313f', IW = '#7a5a3a';
  const P0 = DOLPHIN_Y + DOLPHIN_H;                                  // pedestal: moulded cap, panelled block, plinth
  for (let y = P0; y < 263; y++) {
    const wd = y > 259 ? 13 : y > P0 + 1 ? 9 : 11;
    rect(x, X - (wd >> 1), y, wd, 1, I); px(x, X - (wd >> 1), y, y === P0 ? IW : IH);
  }
  rect(x, X - 5, P0, 11, 1, IW); rect(x, X - 6, 260, 13, 1, IH);
  rect(x, X - 2, P0 + 3, 5, 4, '#1c1a24'); px(x, X, P0 + 4, IH);    // the panel with its lion mask
  for (let y = 152; y < P0; y++) {                                   // fluted post
    rect(x, X - 1, y, 3, 1, I);
    px(x, X - 1, y, y < 200 ? IW : IH);
    if (y > 214 && (y & 1)) px(x, X, y, '#211f2a');
  }
  for (const y of [DOLPHIN_Y - 4, 212, 184, 157]) { rect(x, X - 2, y, 5, 2, I); px(x, X - 2, y, IW); }
  renderDolphins(x, X);
  rect(x, X - 6, 153, 13, 1, I); px(x, X - 6, 152, IW); px(x, X + 6, 152, IW);   // ladder bar
  px(x, X - 7, 152, I); px(x, X + 7, 152, I);
  for (let k = 0; k < 3; k++) { px(x, X - 2 - k, 151 - k, I); px(x, X + 2 + k, 151 - k, I); }   // scroll brackets
  for (let y = 132; y <= 150; y++) {                                 // tapered four-pane lantern
    const half = y < 134 ? 5 : Math.round(5 - (y - 134) * 2.5 / 16);
    rect(x, X - half, y, half * 2 + 1, 1, I);
    if (y >= 135 && y <= 148 && half > 1) {
      rect(x, X - half + 1, y, half * 2 - 1, 1, y < 140 ? '#fff6dc' : y < 145 ? '#ffe2a0' : '#ffc270');
      px(x, X, y, '#c88a48');
    }
  }
  for (const [y, half] of [[131, 6], [130, 5], [129, 4], [128, 3], [127, 2], [126, 1]]) {
    rect(x, X - half, y, half * 2 + 1, 1, I); px(x, X - half, y, IW);
  }
  rect(x, X, 121, 1, 5, I); px(x, X, 120, IW); px(x, X - 1, 123, I); px(x, X + 1, 123, I);
  return c;
}

/* ================= Red K6 telephone box, lit from inside ================= */
function renderPhone() {
  const [c, x] = makeCanvas(W, H);
  const X0 = PHONE.x, Wd = PHONE.w, X1 = X0 + Wd - 1, T = PHONE.y, B = PHONE.base;
  const RED = '#c8222a', RL = '#ee5048', RD = '#8a141e', RDD = '#560c14';
  rect(x, X0 - 1, B - 3, Wd + 2, 3, '#3e3946'); rect(x, X0 - 1, B - 3, Wd + 2, 1, '#6a6274');   // plinth
  rect(x, X0, T + 9, Wd, B - 3 - (T + 9), RED);
  rect(x, X0, T + 9, 2, B - 3 - (T + 9), RL); rect(x, X1 - 1, T + 9, 2, B - 3 - (T + 9), RD);
  px(x, X1, T + 9, RDD);
  for (let row = 0; row < 6; row++) for (let col = 0; col < 3; col++) {                        // glowing panes
    const gx = X0 + 2 + col * 4, gy = T + 11 + row * 4;
    rect(x, gx, gy, 3, 3, row < 2 ? '#fff6dc' : row < 4 ? '#ffeab8' : '#ffdc9c');
    px(x, gx, gy, '#ffffff');
  }
  rect(x, X0 + 6, T + 19, 3, 4, '#3a2a2a'); px(x, X0 + 7, T + 18, '#2a2020');                     // the telephone inside
  rect(x, X0 + 2, T + 36, Wd - 4, 5, RD); rect(x, X0 + 3, T + 37, Wd - 6, 3, RED);              // kick panel
  px(x, X1 - 2, T + 26, '#e8c070'); px(x, X1 - 2, T + 27, '#b08040');                          // door handle
  rect(x, X0 - 1, T + 8, Wd + 2, 1, RD);                                                          // cornice
  rect(x, X0 + 1, T + 5, Wd - 2, 3, '#f6f0dc');                                                   // lit sign strip
  for (let i = X0 + 2; i < X1 - 1; i++) if (i % 2 === 0 || i === X0 + 5) px(x, i, T + 6, '#4a3a3e');
  rect(x, X0, T + 4, Wd, 1, RL); rect(x, X0 + 1, T + 3, Wd - 2, 1, RED);                          // domed roof
  rect(x, X0 + 2, T + 2, Wd - 4, 1, RED); rect(x, X0 + 4, T + 1, Wd - 8, 1, RL); rect(x, X0 + 6, T, 3, 1, RL);
  px(x, X1 - 2, T + 2, RD); px(x, X1 - 1, T + 3, RD);
  const CX = X0 + (Wd >> 1);                                                                      // K6: gilt crown on the dome over the sign
  rect(x, CX - 2, T + 3, 5, 1, GOLD); px(x, CX + 2, T + 3, GOLDD);
  for (const dx of [-2, 0, 2]) px(x, CX + dx, T + 2, dx ? GOLD : GOLDH);
  px(x, CX, T + 1, GOLDH);
  return c;
}

/* ================= Victorian pub corner (right) ================= */
const GOLD = '#d8ac4c', GOLDH = '#ffe08a', GOLDD = '#8a6424', GRN = '#143424', GRNH = '#2e6044', GRND = '#0a1d13';
const BAY = { x0: 395, x1: 432, y0: 195, y1: 243 }, RWIN = { x0: 452, x1: 475, y0: 195, y1: 243 };
const PUB_WINS = [];
{
  const kinds = [['warm', 'toggle', 'curtain'], ['toggle', 'lamp', 'warm']], r = rng(505);
  [110, 140].forEach((wy, fi) => [398, 426, 454].forEach((wx, ci) => PUB_WINS.push({ id: PUB_WINS.length, x: wx, y: wy,
    w: 14, h: 20, kind: kinds[fi][ci], n: 3 + Math.floor(r() * 4), p: 0.55, off: r() })));
}
function brick(i, y) {
  const row = Math.floor(y / 3);
  if (y % 3 === 2 || (i + (row & 1) * 3) % 6 === 0) return '#241618';
  const v = hash(Math.floor((i + (row & 1) * 3) / 6), row, 3);
  return v < 0.3 ? '#3a2224' : v < 0.8 ? '#472a28' : '#56322c';
}
function bigText(c, s, x0, y0, col, hi, shadow) {            // FONT3 text at 2x, crisp
  const w = s.length * 4;
  const mk = colr => { const [t, tx] = makeCanvas(w, 5); text(tx, s, 0, 0, colr); return t; };
  if (shadow) c.drawImage(mk(shadow), 0, 0, w, 5, x0 + 1, y0 + 1, w * 2, 10);
  c.drawImage(mk(col), 0, 0, w, 5, x0, y0, w * 2, 10);
  c.drawImage(mk(hi), 0, 0, w, 1, x0, y0, w * 2, 2);
}
function glassFill(x, g, pal, side) {                         // dithered warm interior light
  for (let y = g.y0 + 1; y < g.y1; y++) for (let i = g.x0 + 1; i < g.x1; i++) {
    const v = (y - g.y0) / (g.y1 - g.y0) * (pal.length - 1) + (side && side(i) ? 1 : 0);
    const k = Math.floor(v);
    px(x, i, y, pal[Math.min(pal.length - 1, k + (v - k > bayer(i, y) ? 1 : 0))]);
  }
}
function renderPub() {
  const [c, x] = makeCanvas(W, H);
  const r = rng(606), X0 = PUB_X;
  // chimney stack and pots
  for (let y = 86; y < 100; y++) for (let i = 446; i <= 462; i++) px(x, i, y, i >= 461 ? '#241618' : brick(i, y));
  rect(x, 445, 84, 19, 2, '#5a5262'); rect(x, 445, 84, 19, 1, '#7a7284');
  for (const p of [449, 456]) { rect(x, p, 78, 3, 6, '#8a4a38'); rect(x, p, 78, 3, 1, '#b06a50'); px(x, p, 79, '#a85e48'); }
  // brick upper floors with a warm spill from the pub lights below
  for (let y = 104; y < 170; y++) for (let i = X0; i < W; i++) {
    let col = brick(i, y);
    if (col !== '#241618' && y > 146 && bayer(i, y) < (y - 146) / 40) col = '#6a3a2a';
    px(x, i, y, col);
  }
  for (let y = 104; y < 170; y++) {                                      // stone quoins on the corner
    const blk = Math.floor((y - 104) / 4), wq = (blk & 1) ? 3 : 5;
    rect(x, X0, y, wq, 1, (y - 104) % 4 === 3 ? '#4a4250' : blk & 1 ? '#8a8090' : '#9a90a0');
    px(x, X0, y, '#b0a8b8');
  }
  // cornice
  [['#7a7088', 98], ['#5a5268', 99], ['#443c52', 100], ['#3a3346', 101], ['#241f2c', 102]].forEach(([col, y]) => rect(x, X0 - 2, y, W - X0 + 2, 1, col));
  for (let i = X0 - 2; i < W; i += 3) px(x, i, 103, '#3a3346');
  // upstairs sash windows (glass drawn per frame): stone lintels and sills
  for (const w of PUB_WINS) {
    rect(x, w.x - 2, w.y - 3, w.w + 4, 2, '#8a8090'); rect(x, w.x - 2, w.y - 3, w.w + 4, 1, '#aaa2b2');
    px(x, w.x + (w.w >> 1), w.y - 3, '#c8c0d0');
    rect(x, w.x - 1, w.y - 1, w.w + 2, w.h + 1, '#2a2430');
    rect(x, w.x - 2, w.y + w.h, w.w + 4, 2, '#9a90a0'); rect(x, w.x - 2, w.y + w.h + 1, w.w + 4, 1, '#5a5262');
  }
  // fascia cornice and fascia board with gold lettering
  rect(x, X0 - 2, 170, W - X0 + 2, 1, GRNH); rect(x, X0 - 2, 171, W - X0 + 2, 1, GOLD);
  rect(x, X0 - 2, 172, W - X0 + 2, 3, GRN); rect(x, X0 - 2, 175, W - X0 + 2, 1, GRND);
  for (let i = X0; i < W; i += 4) px(x, i, 173, GRNH);
  rect(x, X0, 176, W - X0, 14, GRN);
  rect(x, X0 + 3, 177, W - X0 - 6, 1, GOLDD); rect(x, X0 + 3, 188, W - X0 - 6, 1, GOLDD);
  rect(x, X0 + 3, 177, 1, 12, GOLDD); rect(x, W - 4, 177, 1, 12, GOLDD);
  bigText(x, 'PUB', 424, 178, GOLD, GOLDH, '#050c08');
  for (const ox of [404, 460]) {                                         // little gold diamonds
    px(x, ox, 181, GOLD); px(x, ox - 1, 182, GOLD); px(x, ox + 1, 182, GOLD); px(x, ox, 183, GOLDH); px(x, ox, 182, GOLDH);
    rect(x, ox - 8, 182, 5, 1, GOLDD); rect(x, ox + 4, 182, 5, 1, GOLDD);
  }
  // ground floor
  rect(x, X0, 190, W - X0, 68, GRN);
  rect(x, BAY.x0 - 2, 191, BAY.x1 - BAY.x0 + 5, 3, '#4a4656'); rect(x, BAY.x0 - 2, 191, BAY.x1 - BAY.x0 + 5, 1, '#77728a');
  const PAL = ['#ffe6ac', '#ffcc7c', '#f4aa5a', '#dc8846', '#c06e38'];
  glassFill(x, BAY, PAL, i => i < BAY.x0 + 7 || i > BAY.x1 - 7);
  glassFill(x, RWIN, PAL, null);
  // inside the bar: shelves of bottles, pendant lamps, the counter with its brass rail
  for (const sy of [212, 219]) {
    rect(x, BAY.x0 + 8, sy, BAY.x1 - BAY.x0 - 15, 1, '#7a4220');
    for (let i = BAY.x0 + 9; i < BAY.x1 - 7; i += 2) if (r() < 0.8) {
      const bh = 2 + Math.floor(r() * 2);
      rect(x, i, sy - bh, 1, bh, ['#2e5a3a', '#6a1e22', '#c89a40', '#3a3060', '#8a5a2a'][Math.floor(r() * 5)]);
    }
  }
  rect(x, BAY.x0 + 1, 233, BAY.x1 - BAY.x0 - 1, 10, '#4a2412'); rect(x, BAY.x0 + 1, 233, BAY.x1 - BAY.x0 - 1, 1, '#e8b860');
  for (let i = BAY.x0 + 3; i < BAY.x1; i += 6) rect(x, i, 235, 4, 6, '#5e3018');
  for (const lx of [406, 421]) { rect(x, lx, 196, 1, 7, '#6a3a1a'); rect(x, lx - 1, 203, 3, 1, '#8a4a20'); px(x, lx, 204, '#fffae0'); }
  rect(x, RWIN.x0 + 13, 225, 5, 1, '#5a2e16'); rect(x, RWIN.x0 + 15, 226, 1, 8, '#5a2e16');   // table + lamp
  rect(x, RWIN.x0 + 13, 219, 5, 3, '#fff0c0'); px(x, RWIN.x0 + 15, 222, '#c89040');
  rect(x, RWIN.x0 + 3, 206, 6, 5, '#7a4a22'); rect(x, RWIN.x0 + 4, 207, 4, 3, '#b0703a');         // picture on the wall
  // window frames: side panes of the bay, transoms, small top lights, mullions
  const frame = (i, y0, y1) => rect(x, i, y0, 1, y1 - y0 + 1, GRND);
  for (const g of [BAY, RWIN]) {
    rect(x, g.x0, g.y0, g.x1 - g.x0 + 1, 1, GRND); rect(x, g.x0, g.y1, g.x1 - g.x0 + 1, 1, GRND);
    frame(g.x0, g.y0, g.y1); frame(g.x1, g.y0, g.y1);
    rect(x, g.x0, g.y0 + 8, g.x1 - g.x0 + 1, 1, GRND);
    for (let i = g.x0 + 3; i < g.x1; i += 3) frame(i, g.y0, g.y0 + 8);
    for (let yy = g.y0 + 3; yy < g.y0 + 8; yy += 3) rect(x, g.x0, yy, g.x1 - g.x0 + 1, 1, GRND);
  }
  for (const i of [BAY.x0 + 6, BAY.x0 + 7, BAY.x1 - 7, BAY.x1 - 6]) frame(i, BAY.y0, BAY.y1);
  for (const i of [410, 417]) frame(i, BAY.y0 + 8, BAY.y1);
  frame(RWIN.x0 + 12, RWIN.y0 + 8, RWIN.y1);
  // door with a glowing fanlight and etched glass
  rect(x, 438, 194, 12, 64, GRND);
  for (let y = 195; y <= 200; y++) for (let i = 439; i <= 448; i++) {
    const dd = Math.hypot(i - 443.5, (y - 200.5) * 1.6);
    if (dd < 5.2) px(x, i, y, dd > 4.2 ? GOLDD : ((i - 439) % 3 === 1 && y > 197) ? '#c07a3a' : '#ffd890');
  }
  rect(x, 439, 202, 10, 56, GRN); rect(x, 439, 202, 1, 56, GRNH);
  glassFill(x, { x0: 440, x1: 447, y0: 203, y1: 226 }, ['#ffe0a0', '#f6b868', '#e09a52'], null);
  for (let y = 206; y < 225; y += 4) px(x, 443, y, '#fff6d8');
  for (const [y0, y1] of [[229, 240], [243, 254]]) { rect(x, 440, y0, 8, y1 - y0, '#0f2a1b'); rect(x, 440, y0, 8, 1, GRNH); }
  px(x, 446, 232, GOLDH); px(x, 446, 233, GOLD);
  // pilasters with gold capitals
  for (const p0 of [390, 434, 476]) {
    rect(x, p0, 190, 4, 64, GRN); rect(x, p0, 190, 1, 64, GRNH); rect(x, p0 + 3, 190, 1, 64, GRND);
    for (let y = 196; y < 250; y += 2) px(x, p0 + 2, y, '#0e2419');
    rect(x, p0 - 1, 190, 6, 1, GOLDH); rect(x, p0 - 1, 191, 6, 1, GOLD); rect(x, p0, 192, 4, 1, GOLDD);
    rect(x, p0 - 1, 250, 6, 4, '#0d2217'); rect(x, p0 - 1, 250, 6, 1, GOLD);
  }
  // stallrisers, stone plinth and door step
  for (const [a, b] of [[BAY.x0, BAY.x1], [RWIN.x0, RWIN.x1]]) {
    rect(x, a, 244, b - a + 1, 10, '#0f2a1b'); rect(x, a, 244, b - a + 1, 1, GOLD);
    for (let i = a + 2; i < b - 4; i += 9) { rect(x, i, 246, 7, 6, '#12301f'); rect(x, i, 246, 7, 1, GRNH); rect(x, i, 251, 7, 1, GRND); }
  }
  rect(x, X0, 254, W - X0, 4, '#2c2834'); rect(x, X0, 254, W - X0, 1, '#565064');
  rect(x, 437, 255, 14, 3, '#3a3444'); rect(x, 437, 255, 14, 1, '#6e6880');
  // corner shadow and the bracket for the hanging sign
  rect(x, X0 - 1, 104, 1, 154, '#0c0a12');
  rect(x, 364, 158, X0 - 364, 2, '#15141c'); px(x, 363, 158, '#15141c'); px(x, 362, 157, '#15141c');
  rect(x, 364, 158, X0 - 364, 1, '#3a3646');
  for (let k = 0; k < 14; k++) px(x, X0 - 1 - k, 172 - Math.round(k * 0.9), '#15141c');
  for (let k = 0; k < 6; k++) px(x, 372 + Math.round(3 * Math.cos(k)), 162 + Math.round(2 * Math.sin(k)), '#15141c');
  rect(x, X0 - 2, 156, 2, 18, '#15141c');
  return c;
}
/* the painted hanging sign: gold frame, red field, a crown over a scroll */
const SIGN_W = 23, SIGN_H = 19;
function renderSign() {
  const [c, x] = makeCanvas(SIGN_W, SIGN_H);
  rect(x, 0, 0, SIGN_W, SIGN_H, GOLDD); rect(x, 1, 1, SIGN_W - 2, SIGN_H - 2, GOLD); rect(x, 1, 1, SIGN_W - 2, 1, GOLDH);
  rect(x, 2, 2, SIGN_W - 4, SIGN_H - 4, '#0f2a1b'); rect(x, 3, 3, SIGN_W - 6, SIGN_H - 6, '#6e1a20');
  rect(x, 3, 3, SIGN_W - 6, 1, '#8a2a2a');
  const CROWN = ['.....X.....', 'X...XXX...X', 'XX..XXX..XX', 'XXX.XXX.XXX', 'XXXXXXXXXXX', 'XJXJXJXJXJX', 'XXXXXXXXXXX'];
  CROWN.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') px(x, 6 + i, 4 + j, row[i] === 'J' ? '#e8e4ff' : j < 2 ? GOLDH : GOLD); });
  rect(x, 5, 12, 13, 3, '#eadcbc'); px(x, 4, 13, '#c8b890'); px(x, 18, 13, '#c8b890');
  for (let i = 7; i < 16; i += 2) px(x, i, 13, '#6a4a3a');
  return c;
}
/* hanging flower basket (chains drawn per frame) */
function renderBasket(seed) {
  const [c, x] = makeCanvas(15, 13); const r = rng(seed);
  for (let y = 0; y < 13; y++) for (let i = 0; i < 15; i++) {
    const dx = (i - 7) / 7, dy = (y - 5) / 5.5, v = 1 - dx * dx - dy * dy + (r() - 0.5) * 0.35;
    if (v > 0) px(x, i, y, y < 3 ? '#4a7a3a' : v > 0.5 ? '#1e3a22' : '#2e5a30');
    else if (y > 6 && (i === 2 || i === 11 || i === 6) && r() < 0.7) px(x, i, y, '#2e5a30');   // trailing ivy
  }
  rect(x, 4, 8, 7, 2, '#4a3020'); rect(x, 4, 8, 7, 1, '#6a4a30');
  const FL = ['#f070a0', '#e8404c', '#b068e0', '#fff0f4', '#ffb040'];
  for (let k = 0; k < 16; k++) { const fx = 1 + Math.floor(r() * 13), fy = 1 + Math.floor(r() * 9); if (x.getImageData(fx, fy, 1, 1).data[3]) px(x, fx, fy, FL[Math.floor(r() * FL.length)]); }
  return c;
}

/* ================= Poster hoarding at the bridge's end (the city's one billboard) =================
   A pasted poster panel on a low tube-and-clamp scaffold, two lifts high, that stands on the far footway of Westminster
   Bridge at its Westminster end: the buses pass in front of its legs and the face clears their roofs. Scale: the face
   is a bus and a third tall (a bus is 14 px) and the whole sign, lamps to deck, four buses; in Parliament's storeys
   (its window rows are 16 px apart) that is 1.2 and 3.5. Framed in the pub's green with a gilt bead and lit by three
   small gooseneck lamps, all stepped down to the fog: it is a night poster, well under Big Ben's floodlit stone
   (BILL_NIGHT). The scaffold is a silhouette against the fog, rim-lit on the left by Big Ben's floodlights. The face
   (BILL) is the poster. */
const IRON = { d: '#0f0e15', m: '#18171f', h: '#3a3646', w: '#7a5a3e', r: '#5e4a3c' };   // silhouettes against the lit fog
const BILL_GILT = '#a8853a', BILL_GILTD = '#6e5020';       // the bead under small lamps in fog (the pub's own gilt is brighter)
const BILL_LIT = ['#c09858', '#dcbc7c'];                   // a lamp's lit opening: rim, core (the clock, the Ayrton light and the bridge's globes stay whiter)
const BILL_NIGHT = { level: 0.74, tint: '#383049', mix: 0.12, wear: 0.3, seed: 73 };   // the poster's night grade (core gradeAd): towards the sky's violet, rain-streaked
function line(c, x0, y0, x1, y1, col) {                    // 1 px line, integer endpoints
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  for (let k = 0; k <= n; k++) px(c, Math.round(x0 + (x1 - x0) * k / n), Math.round(y0 + (y1 - y0) * k / n), col);
}
function renderBillboard() {
  const [c, x] = makeCanvas(W, H);
  const L = BILL.x - 2, R = BILL.x + BILL.w + 1, T = BILL.y - 2, B = BILL.y + BILL.h + 1;   // frame box
  const CW = B + 2, BOT = DECK + 3;                         // catwalk row; the bridge deck hides everything below
  // scaffold: three standards (the bridge's lamp at x 253 stands clear in the right-hand bay), ledgers every 9 rows, one
  // diagonal brace per bay (alternating), all behind the bridge's parapet and traffic
  const posts = [L + 3, (L + R) >> 1, R - 3], ledgers = [];
  for (let y = CW + 9; y < BOT; y += 9) ledgers.push(y);
  const rows = [CW + 1, ...ledgers, BOT];
  for (let k = 0; k + 1 < posts.length; k++) for (let j = 0; j + 1 < rows.length; j++) {
    const a = posts[k] + 1, b = posts[k + 1] - 1, y0 = rows[j] + 1, y1 = rows[j + 1] - 1;
    if ((k + j) & 1) line(x, a, y0, b, y1, IRON.m); else line(x, a, y1, b, y0, IRON.m);
  }
  for (const y of ledgers) rect(x, posts[0] - 1, y, posts.at(-1) - posts[0] + 3, 1, IRON.d);
  posts.forEach((p, k) => {
    rect(x, p, CW + 1, 1, BOT - CW - 1, IRON.d);
    for (const y of ledgers) px(x, p, y, IRON.h);            // the clamps catch a little light
    if (k === 0) for (let y = CW + 1; y < BOT; y++) if ((y & 1) || y < CW + 12) px(x, p - 1, y, IRON.r);   // Big Ben's floodlight on the left
  });
  // catwalk: a grated deck just under the frame, with brackets back to the outer standards
  rect(x, L, CW, R - L + 1, 1, IRON.h); rect(x, L, CW + 1, R - L + 1, 1, IRON.d);
  for (let i = L; i <= R; i += 2) px(x, i, CW, IRON.m);
  line(x, L, CW + 1, posts[0], CW + 4, IRON.d); line(x, R, CW + 1, posts.at(-1), CW + 4, IRON.d);
  rect(x, L, B + 1, R - L + 1, 1, '#0c0b12');               // shadow line under the frame
  // frame: green moulding (lamp-lit along the top) with a gilt bead round the poster
  rect(x, L, T, R - L + 1, B - T + 1, GRN);
  rect(x, L, T, R - L + 1, 1, GRNH); rect(x, L, T, 1, B - T + 1, GRNH);
  rect(x, R, T + 1, 1, B - T, GRND); rect(x, L + 1, B, R - L, 1, GRND);
  rect(x, L + 1, T + 1, R - L - 1, 1, BILL_GILT); rect(x, L + 1, T + 1, 1, B - T - 1, BILL_GILTD);
  rect(x, R - 1, T + 1, 1, B - T - 1, BILL_GILTD); rect(x, L + 1, B - 1, R - L - 1, 1, BILL_GILTD);
  for (const [bx, by] of [[L, T], [R, T], [L, B], [R, B]]) px(x, bx, by, BILL_GILTD);   // corner bolts
  rect(x, BILL.x, BILL.y, BILL.w, BILL.h, '#0d0f24');      // (the face itself is painted per frame)
  // gooseneck lamps: arm up from the top rail, shade with its lit opening facing the poster
  for (const lx of BILL_LAMPS) {
    const y = BILL_LAMP_Y;
    rect(x, lx - 1, T - 1, 3, 1, IRON.d);                    // mounting plate
    rect(x, lx, y + 3, 1, T - y - 4, IRON.m); px(x, lx, y + 3, IRON.w);   // arm, warm where the lamp lights it
    rect(x, lx - 1, y, 3, 1, IRON.d);
    rect(x, lx - 2, y + 1, 5, 1, IRON.d); px(x, lx - 2, y + 1, IRON.h);
    rect(x, lx - 2, y + 2, 5, 1, BILL_LIT[0]); rect(x, lx - 1, y + 2, 3, 1, BILL_LIT[1]);
  }
  return c;
}
/* the city's own ad: "PROPER TEA", a china cup on its saucer (steam drawn per frame), lit in scallops from above; painted
   in daylight colours, then night-graded as a whole (BILL_NIGHT) */
const POSTER_PAL = ['#8a765a', '#ab9774', '#cab893', '#e4d5af', '#f6e6bc', '#ffefc6'];   // paper, then the lamps' warm scallops on it
const CUP = { x: 1, y: 8, rows: [
  '..NNNNNNNNNNN..', '..NTTTTTTTTTN..', '..NWWWWWWWWSNNN', '..NRRRRRRRRRN.N', '...NWWWWWWSNNNN',
  '....NWWWWSN....', '.....NNNNN.....', '.SWWWWWWWWWWWS.', '..NNNNNNNNNNN..'] };
const CUP_COL = { N: '#1f2748', T: '#6e3c1e', W: '#fffaf0', S: '#cbbea6', R: '#c8262c' };
const TEA = ['111111..11111...1111.', '111111..11111..111111', '..11....11.....11..11', '..11....11.....11..11',
  '..11....1111...111111', '..11....1111...111111', '..11....11.....11..11', '..11....11.....11..11',
  '..11....11111..11..11', '..11....11111..11..11'];        // chunky poster lettering, 2 px strokes
function renderPoster(dim) {                                // dim: the right-hand lamp is out
  const { w, h } = BILL;
  const c = ditherFill(w, h, POSTER_PAL, (i, j) => {       // flat paper, a crisp scallop of light under each lamp
    let cone = 0;
    BILL_LAMPS.forEach((lx, k) => {
      const dx = (i - (lx - BILL.x)) / (4 + j * 0.7);
      cone = Math.max(cone, (k === 2 && dim ? 0.25 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - j / 11));
    });
    let v = 0.6 + 0.2 * smooth(0.2, 0.32, cone) + 0.2 * smooth(0.55, 0.67, cone);
    if (dim) v -= 0.22 * Math.exp(-(((i - (BILL_LAMPS[2] - BILL.x)) / 11) ** 2)) * (1 - j / (h + 12));
    return v;
  });
  const x = c.getContext('2d');
  CUP.rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') px(x, CUP.x + i, CUP.y + j, CUP_COL[row[i]]); });
  px(x, CUP.x + 4, CUP.y + 1, '#9a5a2e');                   // a glint on the tea
  text(x, 'PROPER', 18, 1, '#1f2748');
  for (const [dx, dy, col] of [[1, 1, '#6a1a1e'], [0, 0, '#c8262c']])
    TEA.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') px(x, 18 + i + dx, 7 + j + dy, j === 0 && !dx ? '#e65448' : col); });
  return gradeAd(c, BILL_NIGHT);
}

/* ================= Plane-tree branch across the top-left corner ================= */
function renderBranch() {
  const [c, x] = makeCanvas(W, 52);
  const r = rng(77), leaves = [];
  const LEAF = ['#2c1816', '#44221a', '#6e3418', '#9a4a1e', '#c06a28', '#d89a3a'];
  const limb = (x0, y0, ang, len, wd, depth) => {
    let xx = x0, yy = y0, a = ang;
    for (let s = 0; s < len; s++) {
      a += (r() - 0.5) * 0.22 + 0.006;
      xx += Math.cos(a); yy += Math.sin(a);
      const w = Math.max(1, Math.round(wd * (1 - s / len * 0.8)));
      rect(x, Math.round(xx), Math.round(yy), 1, w, s % 5 === 0 ? '#2a1e22' : '#1a1318');
      if (w > 1) px(x, Math.round(xx), Math.round(yy), '#3a2a2a');
      if (depth < 3 && s > 5 && r() < 0.08) limb(xx, yy, a + (r() < 0.6 ? 0.6 : -0.5) + (r() - 0.5) * 0.4, len * (0.3 + r() * 0.3), w, depth + 1);
      if (s > len * 0.35 && r() < 0.22) leaves.push([xx + (r() - 0.5) * 8, yy + 1 + r() * 6]);
    }
    for (let k = 0; k < 5; k++) leaves.push([xx + (r() - 0.5) * 9, yy + (r() - 0.3) * 7]);
  };
  limb(-3, 5, 0.12, 92, 4, 0);
  limb(-3, 0, 0.03, 48, 2, 1);
  for (const [lx, ly] of leaves) {
    const k = Math.min(5, Math.floor(r() * 3.4 + (ly / 50) * 1.8)), col = LEAF[k];
    const X = Math.round(lx), Y = Math.round(ly);
    px(x, X, Y, col); px(x, X + 1, Y, col); px(x, X - 1, Y, col); px(x, X, Y + 1, col); px(x, X, Y - 1, LEAF[Math.max(0, k - 1)]);
    if (r() < 0.4) px(x, X + 1, Y + 1, LEAF[Math.min(5, k + 1)]);
  }
  return c;
}

function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 8; d[o + 1] = 6; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.45 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Autumn plane-tree leaves (3 depths, flutter and flip) ================= */
const LEAF_COLS = [
  { base: '#d8682a', dark: '#8e3a18', light: '#f6a650', back: '#b0522a', vein: '#e88a4a' },
  { base: '#9a5a2a', dark: '#5a3216', light: '#c8864a', back: '#7a4624', vein: '#aa6a3a' },
  { base: '#e0b040', dark: '#9a6a1e', light: '#fadc7c', back: '#bc8e36', vein: '#eec060' },
];
const LEAF_SHAPES = [
  ['11', '10'],
  ['10101', '11111', '01110', '00100'],
  ['000010000', '010010010', '011010110', '111111111', '011111110', '001111100', '000111000', '000010000'],
];
function makeLeaf(lc, size, back) {
  const S = LEAF_SHAPES[size], w = S[0].length, h = S.length, mid = w >> 1;
  const [c, x] = makeCanvas(w, h);
  S.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '1') {
    let col = back ? lc.back : lc.base;
    if (size > 0 && i === mid && j > 0) col = back ? lc.vein : lc.dark;
    else if (!back && size > 0 && i < mid && j < h - 3 && (i + j) % 3 === 0) col = lc.light;
    else if (size === 2 && (j === h - 3 || i === 0 || i === w - 1)) col = back ? lc.dark : lc.dark;
    px(x, i, j, col);
  } });
  return c;
}
const LEAVES = [[], [], []];
{
  const r = rng(4343);
  [[26, 7, 10, 4, 24], [16, 10, 13, 7, 40], [8, 13, 16, 12, 60]].forEach(([count, nMin, nMax, amp, drift], size) => {
    for (let i = 0; i < count; i++) LEAVES[size].push({
      id: size * 100 + i, size, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 36 + Math.floor(r() * 30), p2: r(), kr: 20 + Math.floor(r() * 25), p3: r(),
      kf: 26 + Math.floor(r() * 40), p4: r(), amp: amp * (0.7 + r() * 0.6), drift: drift * (0.5 + r()), col: Math.floor(r() * 3),
    });
  });
}

/* ================= Drizzle (3 depths), splashes and rings ================= */
const RAIN_LIGHTS = [
  { x: LAMP.x, y: LAMP.y, r: 50 }, { x: PHONE.x + 7, y: 234, r: 22 }, { x: 414, y: 222, r: 34 }, { x: 463, y: 222, r: 22 },
  { x: BB.cx, y: BB.clockY, r: 26 }, ...BR_LAMPS.map(lx => ({ x: lx, y: 162, r: 8 })),
].map(l => ({ ...l, r2: l.r * l.r }));
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BILL.x + (BILL.w >> 1), y: BILL.y - 4, r: 16, r2: 16 * 16 }];   // + the hoarding's small lamps
// a picked weather (site/js/engine/weather.js): London draws its own drizzle and leaves; snow lands on the far bank, the river
// and past the bottom of the frame, and catches the same lamps as the drizzle
const WEATHER = weatherPlans(['rain', 'leaves']);
const WX_LAND = [[150, 196], [WL + 2, EMB - 1], [H + 4, H + 4]];
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
const WX_BB = { land: WX_LAND, lights: RAIN_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
function litAt(x, y, lights) {
  for (const l of lights) { const dx = x - l.x, dy = y - l.y; if (dx * dx + dy * dy < l.r2) return true; }
  return false;
}
function nearLand(x, a, b) {
  if (x >= PHONE.x && x < PHONE.x + PHONE.w && a < 0.5) return PHONE.y + 1;
  if (x >= LAMP.x - 6 && x <= LAMP.x + 6 && a < 0.3) return 127;
  if (x >= PUB_X) return 259 + Math.floor(b * 10);
  if (a < 0.18) return EMB;
  return PAVE + 2 + Math.floor(b * (H - PAVE - 4));
}
const RAIN = [
  { count: 170, nMin: 150, nMax: 180, fall: 200, ff: 1, len: 2, slope: 0.12, col: 'rgba(150,146,186,0.3)', warm: 'rgba(255,200,130,0.45)', splash: '#9a96ba',
    land: (x, a) => 150 + Math.floor(a * 46) },
  { count: 110, nMin: 190, nMax: 220, fall: 250, ff: 0.9, len: 3, slope: 0.16, col: 'rgba(172,170,208,0.38)', warm: 'rgba(255,205,140,0.6)', splash: '#aaa8cc',
    land: (x, a) => WL + 2 + Math.floor(a * (EMB - WL - 3)) },
  { count: 50, nMin: 240, nMax: 270, fall: 300, ff: 0.82, len: 5, slope: 0.22, col: 'rgba(200,200,232,0.5)', warm: 'rgba(255,214,150,0.75)', splash: '#c8c8e8',
    land: nearLand },
];
RAIN.forEach((L, li) => {
  const r = rng(200 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
const RINGS = [], PUDDLE_RINGS = [];
{ const r = rng(71); for (let i = 0; i < 34; i++) RINGS.push({ id: i, n: 90 + Math.floor(r() * 40), ph: r() }); }
{ const r = rng(72); for (let i = 0; i < 14; i++) PUDDLE_RINGS.push({ id: 50 + i, n: 70 + Math.floor(r() * 30), ph: r() }); }

/* ================= Fog banks: ordered-dithered, drifting, density breathing =================
   Each bank is pre-rendered at FOG_LEVELS+1 densities; every level is a 4-step ordered-dithered alpha mask
   (tinted warmer toward the lit city below). Per frame we crossfade the two levels around the current
   density, so the fog thickens and thins smoothly while it drifts. */
const FOG_DEFS = [
  { seed: 5, y: 118, h: 84, k: 1, dir: 1, top: '#7c7690', bot: '#a8969e', alpha: 0.72,
    dens: t => 0.62 + 0.22 * wave(t, 3, 0.1) + 0.08 * wave(t, 7, 0.6) },
  { seed: 8, y: 170, h: 70, k: 2, dir: -1, top: '#8e88a2', bot: '#a8a0b4', alpha: 0.62,
    dens: t => 0.56 + 0.24 * wave(t, 4, 0.35) + 0.08 * wave(t, 9, 0.2) },
];
const FOG_LEVELS = 10;
function makeFog(def) {
  const h = def.h, n = W * h, F = new Float32Array(n), A = hex(def.top), B = hex(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.6);
    const nz = 0.48 + 0.34 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.05) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.19 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.41) + 0.06 * Math.sin(TAU * 23 * x / W - y * 0.7);
    F[y * W + x] = prof * clamp(nz, 0, 1);
  }
  const levels = [];
  for (let k = 0; k <= FOG_LEVELS; k++) {
    const D = k / FOG_LEVELS * 1.25;
    const [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data;
    for (let p = 0; p < n; p++) {
      const x = p % W, y = (p / W) | 0;
      const q = Math.floor(Math.min(1, F[p] * D) * 4 + bayer(x, y)) / 4;
      if (q <= 0) continue;
      const m = y / (h - 1), o = p * 4;
      d[o] = A[0] + (B[0] - A[0]) * m; d[o + 1] = A[1] + (B[1] - A[1]) * m; d[o + 2] = A[2] + (B[2] - A[2]) * m;
      d[o + 3] = Math.round(Math.min(1, q) * 255);
    }
    cx.putImageData(img, 0, 0);
    levels.push(c);
  }
  return { ...def, levels };
}

/* ================= Pigeons ================= */
const PIGEON = {   // 7x5, facing right: G body, D wing, H head, N neck, B beak, L legs
  up: ['.....HH', '....HHB', '.GDDGN.', 'GGDDGG.', '..L.L..'],
  peck: ['.......', '.GDDG..', 'GGDDGNH', 'GGGGGHB', '..L.L..'],
  walk: ['.....HH', '....HHB', '.GDDGN.', 'GGDDGG.', '.L...L.'],
};
const PIG_COL = { G: '#8a8a9e', D: '#626278', H: '#9a9ab0', N: '#6a9a86', B: '#d8b890', L: '#c86058' };
function makePigeon(rows, flip) {
  const w = rows[0].length, [c, x] = makeCanvas(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] !== '.') px(x, flip ? w - 1 - i : i, j, PIG_COL[row[i]]); });
  return c;
}
/* the flock that lives on Elizabeth Tower */
const PERCHES = [[199, 73], [202, 73], [205, 73], [210, 73], [213, 73], [198, 89], [201, 89], [208, 89], [211, 89], [214, 89],
  [200, 185], [205, 185], [211, 185], [190, 149], [194, 149], [168, 149], [181, 135], [185, 141], [131, 136], [134, 141]];
const FLOCK = PERCHES.map(([x, y], i) => {
  const r = rng(900 + i);
  return { x, y, i, laps: 1 + (r() < 0.45 ? 1 : 0), phi: r() * TAU, rx: 22 + r() * 36, ry: 12 + r() * 20,
    bx: (r() < 0.5 ? -1 : 1) * (30 + r() * 60), by: 18 + r() * 40, del: r() * 0.05 };
});
const FLAP = [['1.....1', '.11.11.', '...2...'], ['.......', '1112111', '...2...'], ['.......', '.11211.', '1.....1']];

/* ================= Smoke puffs ================= */
const PUFFS = [];
for (let pr = 1; pr <= 6; pr++) {
  const s = pr * 2 + 1; const [c, x] = makeCanvas(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - pr, y - pr) / pr;
    if (d < 1 && (1 - d) * 1.6 > bayer(i, y) * 0.9) px(x, i, y, y < pr * 0.7 ? '#8a8298' : '#666078');
  }
  PUFFS[pr] = c;
}
const SMOKE = [];
{ const r = rng(61); for (let i = 0; i < 22; i++) SMOKE.push({ n: 30 + Math.floor(r() * 8), ph: r(), k: 18 + Math.floor(r() * 18), p: r(), pot: i & 1 }); }

/* ================= Timed details ================= */
const LAMP_FLICK = [[88, 89.6], [171.3, 173]];
const PHONE_FLICK = [[132, 134.4], [205.5, 206.7]];
const PLANE = { t0: 150, t1: 206 };
const FLAG = ['WBBWRWBBW', 'BWBWRWBWB', 'WWWWRWWWW', 'RRRRRRRRR', 'WWWWRWWWW', 'BWBWRWBWB'];
const FLAG_COL = { W: '#e8e4f0', B: '#2a347a', R: '#c8262c' };
const STREAKS = [
  { x: BB.cx, w: 5, col: '#fff0c0', a: 0.42, seed: 1 }, { x: VT_CX, w: 4, col: '#ffc860', a: 0.26, seed: 2 },
  ...PAVIL.map(([a, b], i) => ({ x: (a + b) >> 1, w: 4, col: '#ffc050', a: 0.3, seed: 3 + i })),
  ...TERRACE_LAMPS.map((lx, i) => ({ x: lx, w: 2, col: '#ffd890', a: 0.32, seed: 8 + i })),
  ...BR_LAMPS.map((lx, i) => ({ x: lx, w: 3, col: '#ffd490', a: 0.4, seed: 14 + i })),
  { x: EYE.cx, w: 7, col: '#ff7a92', a: 0.16, seed: 30 },
];

/* ================= Pre-render everything static ================= */
/* crop a full-screen layer to its painted bounding box so each frame blends only the pixels it needs */
function trim(c) {
  const w = c.width, h = c.height, d = c.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) if (d[(y * w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  if (x1 < 0) return { c, x: 0, y: 0 };
  const [t, tx] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  tx.drawImage(c, x0, y0, t.width, t.height, 0, 0, t.width, t.height);
  return { c: t, x: x0, y: y0 };
}
const blit = (L, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(L.c, L.x, L.y); ctx.globalAlpha = 1; };
let PARL_R, HL, SKY_C, MOON_C, CLOUDS, FAR, HAZE_C, PARL, SOUTH, EYEF, EYE_GLOW, CAP_C, BRIDGE, WATER_C, BOAT_C,
  BUS_C, CAB_C, GROUND_C, LAMP_C, PHONE_C, PUB_C, SIGN_C, BASKETS, BRANCH_C, VIG_C, LEAF_SPR, FOGS, PIG, DIAL_LIT,
  BILL_C, POSTERS, PITCH, GHOST_C, SWAN_C, FOX_C;
function prerender() {
  HL = {
    moon: makeHalo(46, '#a8a0c8', 0.2, 1.8), moonCore: makeHalo(14, '#e0dcf0', 0.3, 1.5),
    clock: makeHalo(34, '#ffe2a0', 0.3, 1.6), clockCore: makeHalo(12, '#fff6d8', 0.5, 1.4), bong: makeHalo(64, '#ffe0a0', 0.2, 1.5),
    tower: makeHalo(70, '#ffb45a', 0.15, 1.6), parl: makeHalo(56, '#ffb050', 0.13, 1.6), vtower: makeHalo(52, '#ffb45a', 0.13, 1.6),
    ayrton: makeHalo(8, '#fff0c0', 0.55, 1.2), blamp: makeHalo(10, '#ffd490', 0.34), tlamp: makeHalo(7, '#ffd490', 0.32),
    lamp: makeHalo(64, '#ffb866', 0.26, 1.7), lampCore: makeHalo(18, '#ffe6b0', 0.5, 1.4),
    phone: makeHalo(30, '#ff5a48', 0.2, 1.6), phoneCore: makeHalo(13, '#ffe8c0', 0.26, 1.4),
    pub: makeHalo(46, '#ffac58', 0.2, 1.6), bulb: makeHalo(7, '#ffd08a', 0.4), win: makeHalo(12, '#ffb45a', 0.16),
    head: makeHalo(8, '#fff4d0', 0.5, 1.2), tail: makeHalo(4, '#ff3a30', 0.5, 1), red: makeHalo(4, '#ff3030', 0.6, 1),
    cab: makeHalo(4, '#ffb040', 0.5, 1), eyeHub: makeHalo(10, '#ffd0e0', 0.35, 1.4), chase: makeHalo(5, '#ffd6e0', 0.55, 1.2),
    boat: makeHalo(16, '#ffc27a', 0.22), mast: makeHalo(4, '#ffffff', 0.5, 1), strobe: makeHalo(5, '#ffffff', 0.7, 1),
    county: makeHalo(36, '#ffb870', 0.08, 1.5),
    billGlow: makeHalo(24, '#e0a868', 0.08, 1.6), billLamp: makeHalo(5, '#f0c890', 0.18, 1.3),   // half the tower's glow, and smaller
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR = renderFar(); HAZE_C = renderHaze();
  PARL = renderParliament(false); PARL_R = renderParliament(true).c; SOUTH = renderSouthBank();
  EYEF = renderEyeFrames(); EYE_GLOW = makeRingHalo(EYE.r, 10, '#ff5a7a', 0.2); CAP_C = renderCapsule();
  BRIDGE = renderBridge(); WATER_C = renderWater(); BOAT_C = renderBoat();
  const bus = renderBus(7), bus2 = renderBus(8);
  BUS_C = [bus, flipH(bus2)];
  const cab = renderCab(); CAB_C = [cab, flipH(cab)];
  GROUND_C = renderGround(); LAMP_C = renderLamp(); PHONE_C = renderPhone(); PUB_C = renderPub();
  SIGN_C = renderSign(); BASKETS = [renderBasket(3), renderBasket(4)]; BRANCH_C = renderBranch(); VIG_C = renderVignette();
  GHOST_C = GHOST_ROWS.map(rows => spriteOf(rows, GHOST_KEY)); SWAN_C = spriteOf(SWAN_ROWS, SWAN_KEY); FOX_C = [spriteOf(FOX_ROWS, FOX_KEY), spriteOf(['.d........', ...FOX_ROWS.slice(1)], FOX_KEY)];
  BILL_C = trim(renderBillboard()); POSTERS = [renderPoster(false), renderPoster(true)];
  PITCH = makePitch(BILL.w, BILL.h, { accent: '#e8413c' });
  LEAF_SPR = LEAF_COLS.map(lc => [0, 1, 2].map(s => [makeLeaf(lc, s, false), makeLeaf(lc, s, true)]));
  FOGS = FOG_DEFS.map(makeFog);
  PIG = {};
  for (const k in PIGEON) PIG[k] = [makePigeon(PIGEON[k], false), makePigeon(PIGEON[k], true)];
  const [dc, dx] = makeCanvas(15, 15); drawDial(dx, 7, 7, true); DIAL_LIT = dc;
  FAR.c = trim(FAR.c); HAZE_C = trim(HAZE_C); PARL.c = trim(PARL.c); SOUTH.c = trim(SOUTH.c);
  BRIDGE.body = trim(BRIDGE.body); BRIDGE.front = trim(BRIDGE.front);
  GROUND_C = trim(GROUND_C); LAMP_C = trim(LAMP_C); PHONE_C = trim(PHONE_C); PUB_C = trim(PUB_C); BRANCH_C = trim(BRANCH_C);
}

/* ================= Per-frame: sky and far layers ================= */
function drawScroll(cv, y, k, t) {
  const off = scroll(t, k, W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(492 - u * 510), y = Math.round(44 + u * 18);
  rect(ctx, x, y, 4, 1, '#2a2634'); px(ctx, x + 1, y - 1, '#2a2634');
  halo(HL.head, x - 1, y, 0.5);
  px(ctx, x - 1, y, '#fff8e8');
  if (frac(t / 1.2) < 0.1) { px(ctx, x + 2, y, '#ffffff'); halo(HL.strobe, x + 2, y, 0.8); }
  if (frac(t / 1.5 + 0.3) < 0.5) px(ctx, x + 3, y - 1, '#ff3a3a');
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.15 * wave(t, 4));
  halo(HL.moonCore, MOON.x, MOON.y);
  ctx.globalAlpha = 0.8; ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7); ctx.globalAlpha = 1;
  drawScroll(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  drawPlane(t);
  drawScroll(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t);
  drawScroll(CLOUDS[2].c, CLOUDS[2].y, CLOUDS[2].k, t);
}
function drawFar(t) {
  blit(FAR.c);
  for (const w of FAR.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) px(ctx, w.x, w.y, w.col);
  const on = frac(t / 2) < 0.5;
  px(ctx, CRANE.jib0, CRANE.top - 1, on ? '#ff3a3a' : '#5a1818'); px(ctx, CRANE.x + 1, CRANE.top - 10, on ? '#5a1818' : '#ff3a3a');
  halo(HL.red, on ? CRANE.jib0 : CRANE.x + 1, on ? CRANE.top - 1 : CRANE.top - 10, 0.7);
  blit(HAZE_C, 0.24);
}
function drawFog(f, t) {
  const D = clamp(f.dens(t), 0, 0.999) * FOG_LEVELS, k = Math.floor(D), fr = D - k;
  let off = scroll(t, f.k, W);
  if (f.dir < 0) off = (W - off) % W;
  for (const [cv, a] of [[f.levels[k], 1 - fr], [f.levels[k + 1], fr]]) {
    if (a * f.alpha < 0.004) continue;
    ctx.globalAlpha = a * f.alpha;
    ctx.drawImage(cv, off - W, f.y); ctx.drawImage(cv, off, f.y);
  }
  ctx.globalAlpha = 1;
}
function drawLeaf(l, t) {
  const { cyc, u } = cycle(t, l.n, l.ph);
  const S = LEAF_SHAPES[l.size], w = S[0].length, h = S.length, sw = wave(t, l.ks, l.p2);
  const y = -12 + u * (H + 24) - Math.abs(sw) * (l.size + 1) * 1.5;
  const x = -30 + hash(l.id, cyc, 5) * (W + 20) + u * l.drift + sw * l.amp;
  if (x < -12 || x > W + 12) return;
  const fl = Math.cos(TAU * (l.kf * t / LOOP + l.p4)), spr = LEAF_SPR[(l.col + cyc) % 3][l.size][fl < 0 ? 1 : 0];
  if (l.size === 0) { ctx.drawImage(spr, Math.round(x), Math.round(y)); return; }
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y));
  ctx.rotate(sw * 0.9 + 0.3 * wave(t, l.kr, l.p3));
  ctx.scale(1, Math.max(0.25, Math.abs(fl)));
  ctx.drawImage(spr, -(w >> 1), -(h >> 1));
  ctx.restore();
}
function drawRain(L, t, lights) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -24 + hash(d.id, cyc, 11) * (W + 24 + L.fall * L.slope);
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12), hash(d.id, cyc, 13));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1) continue;
      ctx.fillStyle = litAt(xh, yh, lights) ? L.warm : L.col;
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else {
      const s = (u - L.ff) / (1 - L.ff), big = L.len > 4;
      const hgt = Math.sin(Math.PI * s) * (big ? 2.5 : 1.2), spread = 1 + s * (big ? 2.5 : 1.2);
      ctx.globalAlpha = 1 - s;
      ctx.fillStyle = litAt(xl, yl, lights) ? L.warm : L.splash;
      const Y = Math.round(yl - 1 - hgt);
      ctx.fillRect(Math.round(xl - spread), Y, 1, 1); ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
      if (s < 0.35) ctx.fillRect(Math.round(xl), Math.round(yl - 1), 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}

/* ================= Per-frame: South Bank, the Eye, Parliament, Big Ben ================= */
function drawEye(t) {
  blit(SOUTH.c);
  for (const w of SOUTH.wins) if (hash(w.id + 300, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) rect(ctx, w.x, w.y, 1, 2, '#d8a460');
  halo(HL.county, 420, 162, 1);
  halo(EYE_GLOW, EYE.cx, EYE.cy, 0.8 + 0.2 * wave(t, 6));
  ctx.drawImage(EYEF.frames[step(t, EYE_NF)], EYE.cx - EYEF.o, EYE.cy - EYEF.o);
  const rot = t / LOOP * CAP_STEP, rc = EYE.r + 2.5;
  for (let i = 0; i < CAPS; i++) {
    const a = i * CAP_STEP + rot;
    ctx.drawImage(CAP_C, Math.round(EYE.cx + Math.cos(a) * rc) - 2, Math.round(EYE.cy + Math.sin(a) * rc) - 1);
  }
  const ca = TAU * 20 * t / LOOP;                              // chase lights running round the rim
  for (let j = 0; j < 4; j++) {
    const a = ca + j * TAU / 4;
    for (let k = 3; k >= 0; k--) {
      const b = a - k * 0.03;
      px(ctx, Math.round(EYE.cx + Math.cos(b) * EYE.r), Math.round(EYE.cy + Math.sin(b) * EYE.r), k === 0 ? '#ffffff' : '#ffb8c6');
    }
    halo(HL.chase, EYE.cx + Math.cos(a) * EYE.r, EYE.cy + Math.sin(a) * EYE.r, 0.8);
  }
  halo(HL.eyeHub, EYE.cx, EYE.cy, 0.9);
}
function bongLevel(t) {
  let b = 0;
  for (const tb of BONGS) { const dt = t - tb; if (dt >= 0 && dt < 3) b = Math.max(b, Math.exp(-dt * 1.3) * (1 - dt / 3)); }
  return b;
}
function drawParliament(t, clock) {
  halo(HL.vtower, VT_CX, VT.top + 54, 0.95);                   // floodlight glow round the whole tower
  PAVIL.forEach(([a, b], i) => halo(HL.parl, (a + b) >> 1, 168, 0.9 + 0.1 * wave(t, 5, i * 0.3)));
  halo(HL.tower, BB.cx, 124, 0.95);
  blit(PARL.c);
  for (const w of PARL.wins) if (hash(w.id + 7, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < w.p) {
    gothicWin(ctx, w.x, w.y, w.w, w.h, WARM2); gothicWin(ctx, w.x, w.y, w.w, Math.max(2, w.h >> 1), WARM);
  }
  TERRACE_LAMPS.forEach((lx, i) => halo(HL.tlamp, lx, PBASE - 4, 0.85 + 0.15 * wave(t, 500 + i * 29)));
  drawGhost(t);
  for (let i = 0; i < 9; i++) {                                // the flag on Victoria Tower
    const dy = Math.round(Math.sin(TAU * 30 * t / LOOP - i * 0.8) * (i / 8) * 1.3);
    for (let j = 0; j < 6; j++) px(ctx, VT_CX + 1 + i, VT.top - 25 + j + dy, FLAG_COL[FLAG[j][i]]);
  }
  const b = bongLevel(t);
  if (b > 0.02) {
    ctx.globalAlpha = b; ctx.drawImage(DIAL_LIT, BB.cx - 7, BB.clockY - 7);
    ctx.globalAlpha = b * 0.85;
    for (const ox of [-4, 0, 4]) rect(ctx, BB.cx + ox - 1, 81, 3, 7, '#ffb050');
    ctx.globalAlpha = 1;
    halo(HL.bong, BB.cx, 86, b);
  }
  hands(ctx, BB.cx, BB.clockY, clock, 610, 3, 4, '#1a120c');   // on the day face and the lit one alike
  halo(HL.clock, BB.cx, BB.clockY, 0.8 + 0.08 * wave(t, 12) + b * 0.7);
  halo(HL.clockCore, BB.cx, BB.clockY, 0.75 + b);
  halo(HL.ayrton, BB.cx, 52, 0.75 + 0.25 * wave(t, 16));
  for (const tb of BONGS) {                                    // faint sound rings from the belfry
    const dt = t - tb;
    if (dt < 0 || dt > 2.6) continue;
    const rr = 7 + dt * 44, n = Math.floor(rr * 2.2);
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890';
    for (const [dr, a0] of [[0, 0.5], [-2, 0.22]]) {
      ctx.globalAlpha = (1 - dt / 2.6) * a0;
      for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(BB.cx + Math.cos(a) * (rr + dr)), Math.round(84 + Math.sin(a) * (rr + dr) * 0.8), 1, 1); }
    }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
/* hidden things: the ghost in the tower's archway, the swan on the river, the fox on the pub's roof */
function spriteOf(rows, key) {                               // rows of letters and their colours ('.' clear), drawn once
  const [c, x] = makeCanvas(rows[0].length, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (key[row[i]]) px(x, i, j, key[row[i]]); });
  return c;
}
const GHOST_ROWS = [['.ggg.', 'ggggh', 'gkgkh', 'ggggh', 'ggggh', 'g.g.h'], ['.ggg.', 'ggggh', 'gkgkh', 'ggggh', 'ggggh', '.g.gh']];
const GHOST_KEY = { g: '#d4dcec', h: '#a8b2cc', k: '#1e1a2a' };
function drawGhost(t) {
  const w = GHOST_UP.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const tau = t - w[0];
  ctx.globalAlpha = 0.8 * smooth(0, 2, tau) * (1 - smooth(w[1] - w[0] - 2, w[1] - w[0], tau));
  ctx.drawImage(GHOST_C[step(t, 240) & 1], GHOST.x + Math.round(wave(t, 20, 0.4) * 0.8), GHOST.y + Math.round(wave(t, 30, 0.1)));   // its hem stirs, it bobs
  ctx.globalAlpha = 1;
}
const SWAN_ROWS = ['.ww....', 'ow.....', '.w..ws.', '.wwwwws', '..wsss.'];
const SWAN_KEY = { w: '#d2d2e0', s: '#9a9cb6', o: '#c87a3a' };
function drawSwan(t) {
  const x = SWAN.x + Math.round(wave(t, 4, 0.2) * 1.2), y = SWAN.y + (wave(t, 48, 0.3) > 0.6 ? 1 : 0);   // it drifts a little, and rides the ripples
  ctx.globalAlpha = 0.22;                                      // its reflection
  rect(ctx, x + 2, y + 5, 4, 1, SWAN_KEY.w); rect(ctx, x + 1, y + 6, 1, 2, SWAN_KEY.w);
  ctx.globalAlpha = 1;
  ctx.drawImage(SWAN_C, x, y);
}
const FOX_ROWS = ['.d.d......', '.fff......', 'wfkf......', '.ffff..dd.', '.wffff.dfw', '.wfffddfww', '.f.fd.....'];
const FOX_KEY = { f: '#9c5830', d: '#663824', w: '#d2c8be', k: '#140e0c' };
function drawFox(t) {
  ctx.drawImage(FOX_C[hash(4, step(t, 24), 6) < 0.3 ? 1 : 0], FOX.x, FOX.y);   // an ear folds back now and then
}
function drawFlock(t) {
  const flying = t >= FLOCK_T0 && t < FLOCK_T1;
  for (const b of FLOCK) {
    let s = flying ? clamp(((t - FLOCK_T0) / (FLOCK_T1 - FLOCK_T0) - b.del) / (1 - 2 * b.del), 0, 1) : 0;
    if (s <= 0 || s >= 1) {                                    // perched: body + head that turns now and then
      rect(ctx, b.x, b.y, 2, 1, '#34303e');
      px(ctx, b.x + (hash(b.i, step(t, 240), 8) < 0.5 ? 0 : 1), b.y - 1, '#6e687e');
      continue;
    }
    const e = s < 0.5 ? Math.sqrt(Math.sin(Math.PI * s)) : Math.pow(Math.sin(Math.PI * s), 1.5);
    const th = b.phi + TAU * b.laps * s;
    const x = Math.round(b.x + (b.rx * (Math.cos(th) - Math.cos(b.phi)) + b.bx) * e);
    const y = Math.round(b.y + (b.ry * (Math.sin(th) - Math.sin(b.phi)) - b.by) * e);
    const glide = s > 0.15 && s < 0.85 && hash(b.i, step(t, 480), 9) < 0.3;
    const fr = FLAP[glide ? 1 : (Math.floor(t * 9 + b.i * 0.7) % 3)];
    fr.forEach((row, j) => { for (let i = 0; i < 7; i++) if (row[i] !== '.') px(ctx, x - 3 + i, y - 1 + j, row[i] === '2' ? '#34303e' : '#a09ab2'); });
  }
}

/* ================= Per-frame: bridge traffic, river, boat ================= */
function drawVehicle(spr, w, h, a, b, dir, lane, t, isCab) {
  if (t < a || t >= b) return;
  const u = (t - a) / (b - a), x0 = 200, x1 = 404;          // from behind the Tower to behind the pub
  const bx = Math.round(dir > 0 ? x0 - w + (x1 - x0 + w) * u : x1 - (x1 - x0 + w) * u), by = DECK - h - lane;
  ctx.save();
  ctx.beginPath(); ctx.rect(215, 0, W - 215, H); ctx.clip();
  ctx.drawImage(spr[dir > 0 ? 0 : 1], bx, by);
  const fx = dir > 0 ? bx + w : bx - 1, rx = dir > 0 ? bx : bx + w - 1, ly = by + h - (isCab ? 4 : 4);
  halo(HL.head, fx, ly, 0.75); halo(HL.tail, rx, ly, 0.8);
  if (isCab) halo(HL.cab, bx + (w >> 1), by, 0.9);
  else halo(HL.win, bx + (w >> 1), by + 6, 0.6);
  ctx.restore();
}
function drawBridge(t) {
  blit(BRIDGE.body);
  for (const c of CABS) drawVehicle(CAB_C, CAB_W, CAB_H, c.a, c.b, c.dir, c.dir > 0 ? 0 : 1, t, true);
  for (const bs of BUSES) drawVehicle(BUS_C, BUS_W, BUS_H, bs.a, bs.b, bs.dir, bs.dir > 0 ? 0 : 1, t, false);
  blit(BRIDGE.front);
  BR_LAMPS.forEach((lx, i) => halo(HL.blamp, lx, 161, 0.85 + 0.15 * wave(t, 600 + i * 31)));
}
function drawRings(list, t, x0, x1, y0, y1, maxR) {
  for (const g of list) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.45) continue;
    const s = u / 0.45, x = Math.floor(x0 + hash(g.id, cyc, 1) * (x1 - x0)), y = Math.floor(y0 + hash(g.id, cyc, 2) * (y1 - y0));
    if (x > PHONE.x - 3 && x < PHONE.x + PHONE.w + 3 && y > 230) continue;
    const rr = 1 + s * maxR, ry = rr * 0.36, pts = 6 + Math.floor(rr * 2.5);
    ctx.fillStyle = STREAKS.some(st => Math.abs(st.x - x) < st.w + 2) ? '#ffcf80' : '#9a98c4';
    ctx.globalAlpha = (1 - s) * 0.5;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function drawRiver(t, rain) {
  ctx.drawImage(WATER_C, 0, WL);
  const rows = EMB - WL;
  reflect(ctx, t, { top: WL, rows, alpha: 0.46, squash: 1.9, amp: 1.1, grow: 0.08, x: 214, w: W - 214 });
  for (let k = 0; k < rows; k++) {                              // Parliament: soft golden mass, no window detail
    const sy = WL - 1 - Math.floor(k * 1.9);
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (1.1 + k * 0.08) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.7);
    ctx.globalAlpha = 0.4 * (1 - k / (rows + 20));
    ctx.drawImage(PARL_R, 0, sy, 216, 1, off, WL + k, 216, 1);
  }
  ctx.globalAlpha = 1;
  rect(ctx, 0, WL, W, EMB - WL, 'rgba(16,14,36,0.26)');
  rect(ctx, 0, WL, 216, 1, '#1a1622');
  for (let y = WL + 2, n = 0; y < EMB; y += 2, n++) for (let j = 0; j < 4; j++) {   // drifting ripple troughs and glints
    const k = 3 + ((n * 7 + j * 5) % 9), len = 5 + Math.floor(hash(n, j, 41) * 16);
    const x = Math.round(frac(hash(n, j, 40) - k * t / LOOP) * (W + 40)) - 20;
    ctx.globalAlpha = j === 3 ? 0.22 : 0.42;
    rect(ctx, x, y + (j & 1), len, 1, j === 3 ? '#8a84a8' : '#0c0b1a');
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, EMB);
  drawSwan(t);
  if (rain) drawRings(RINGS, t, 0, W, WL + 4, EMB - 3, 5);
}
function drawBoat(t) {
  if (t < BOAT.t0 || t >= BOAT.t1) return;
  const u = (t - BOAT.t0) / (BOAT.t1 - BOAT.t0), bx = Math.round(-46 + u * (W + 52)), by = 213;
  for (let j = 0; j < 9; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.7) * (0.5 + j * 0.12));
    ctx.globalAlpha = 0.3 * (1 - j / 9);
    ctx.drawImage(BOAT_C, 0, 14 - j, 42, 1, bx + off, by + 15 + j, 42, 1);
  }
  ctx.globalAlpha = 1;
  for (let k = 0; k < 18; k++) {                               // foamy wake behind the stern
    const wx = bx + 2 - k * 2 - (k * k >> 3), wy = by + 13 + ((k * 7) % 3) - (k >> 3);
    if (hash(k, step(t, 1440), 17) < 0.65 - k * 0.03) px(ctx, wx, wy, k < 5 ? '#c8c4dc' : '#7a7894');
  }
  ctx.drawImage(BOAT_C, bx, by);
  streak(ctx, t, bx + 26, 4, '#ffd07a', 0.32, 7, by + 15, EMB);
  halo(HL.boat, bx + 26, by + 5, 0.8); halo(HL.mast, bx + 29, by, 0.9); halo(HL.red, bx + 20, by + 8, 0.6);
  for (let k = 0; k < 6; k++) {                                // funnel smoke (a white burst on the horn)
    const { u: v } = cycle(t, 48, k / 6);
    const horn = t >= 124 && t < 126.5;
    const x = bx + 15 - v * 22 - v * v * 10, y = by + 1 - v * 16;
    const rr = Math.min(5, 1 + Math.floor(v * (horn ? 6 : 4)));
    ctx.globalAlpha = (horn ? 0.6 : 0.35) * Math.min(1, v * 6) * (1 - v);
    ctx.drawImage(PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: foreground ================= */
function flick(t, list, id) {
  let v = 1;
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); v = hv > 0.45 ? 1 : hv > 0.2 ? 0.4 : 0.1; }
  return v;
}
function sashWin(w, lit) {
  const { x, y, h } = w, ww = w.w;
  if (lit) { rect(ctx, x, y, ww, h, '#f0a858'); rect(ctx, x, y, ww, h >> 1, '#ffd690'); rect(ctx, x, y + h - 3, ww, 3, '#d88a46'); }
  else { rect(ctx, x, y, ww, h, '#15131d'); for (let k = 0; k < 5; k++) px(ctx, x + 3 + k, y + 9 - k, '#2c2a3a'); }
  ctx.fillStyle = lit ? '#eadcc4' : '#4a4452';
  ctx.fillRect(x + (ww >> 1), y, 1, h); ctx.fillRect(x, y + (h >> 1), ww, 1);
  ctx.fillRect(x + 3, y, 1, h >> 1); ctx.fillRect(x + ww - 4, y, 1, h >> 1);
}
function drawPubWindows(t) {
  for (const w of PUB_WINS) {
    const cx = w.x + (w.w >> 1), cy = w.y + (w.h >> 1);
    if (w.kind === 'lamp') {
      rect(ctx, w.x, w.y, w.w, w.h, '#2a1c24'); rect(ctx, w.x, w.y + w.h - 8, w.w, 8, '#4a2a26');
      rect(ctx, w.x + 2, w.y + w.h - 8, 4, 2, '#ffd890'); rect(ctx, w.x + 3, w.y + w.h - 6, 1, 5, '#3a2418');
      ctx.fillStyle = '#6a5a60'; ctx.fillRect(cx, w.y, 1, w.h); ctx.fillRect(w.x, cy, w.w, 1);
      halo(HL.bulb, w.x + 4, w.y + w.h - 7, 0.8);
      continue;
    }
    const lit = w.kind === 'warm' || w.kind === 'curtain' || hash(w.id + 70, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < w.p;
    sashWin(w, lit);
    if (w.kind === 'curtain') {
      for (const [x0, dir] of [[w.x, 1], [w.x + w.w - 4, -1]]) {
        rect(ctx, x0, w.y, 4, w.h, '#8a2e26');
        for (let y = w.y; y < w.y + w.h; y++) px(ctx, x0 + (dir > 0 ? 1 : 2), y, '#5e1c18');
        px(ctx, dir > 0 ? x0 + 3 : x0, w.y + 12, '#e0b050');
      }
    }
    if (lit) halo(HL.win, cx, cy, 0.8);
  }
}
const SIL = '#3c1e10';
function drawPatrons(t) {
  // two at the bay window, one reading by the lamp in the right window
  const nod = Math.round(0.7 * wave(t, 90, 0.2));
  rect(ctx, 403, 221 + nod, 3, 3, SIL); px(ctx, 402, 222 + nod, SIL); rect(ctx, 403, 224 + nod, 3, 1, SIL);
  rect(ctx, 400, 226, 9, 17, SIL); rect(ctx, 401, 225, 7, 1, SIL);
  const turn = hash(5, step(t, 160), 4) < 0.3 ? 1 : 0;
  rect(ctx, 416 + turn, 218, 3, 4, SIL); px(ctx, 415 + turn, 219, SIL);
  rect(ctx, 413, 223, 10, 20, SIL); rect(ctx, 414, 222, 8, 1, SIL);
  const u = frac(t / 24), lift = smooth(0.1, 0.18, u) * (1 - smooth(0.3, 0.38, u));   // raises a pint
  const gx = Math.round(424 - lift * 4), gy = Math.round(232 - lift * 11);
  rect(ctx, 421, gy + 2, gx - 420, 2, SIL);
  rect(ctx, gx, gy, 2, 3, '#e8a030'); rect(ctx, gx, gy, 2, 1, '#fff4e0');
  const pg = Math.round(0.5 * wave(t, 12, 0.6));
  rect(ctx, 459, 223 + pg, 3, 3, SIL); rect(ctx, 457, 227, 7, 16, SIL);
  rect(ctx, 462, 229, 3, 2, '#f0e6d0');                         // the newspaper
}
function drawBaskets(t) {
  [[397, 194], [471, 194]].forEach(([hx, hy], i) => {
    const dx = Math.round(1.4 * wave(t, 18, i * 0.37) + 0.5 * wave(t, 47, i * 0.2));
    for (let k = 0; k < 5; k++) px(ctx, hx + Math.round(dx * k / 5), hy + k, '#2a2832');
    px(ctx, hx, hy - 1, '#15141c');
    ctx.drawImage(BASKETS[i], hx - 7 + dx, hy + 4);
  });
}
function drawSign(t) {
  const a = 0.09 * wave(t, 20, 0.1) + 0.03 * wave(t, 53, 0.4), top = 160;
  for (const cx of [367, 383]) for (let k = 0; k < 4; k++) px(ctx, Math.round(cx + a * (k + 2)), top + k, '#2a2832');
  for (let j = 0; j < SIGN_H; j++) ctx.drawImage(SIGN_C, 0, j, SIGN_W, 1, 364 + Math.round(a * (j + 6)), top + 4 + j, SIGN_W, 1);
}
/* the hoarding at the bridge's end: glow in the drizzle, structure, the face (own ad or the pitch), then the lamps' halos */
const BILL_FLICK = [[47.4, 49.2], [183.6, 184.6]];          // the right-hand lamp sputters
const STEAM = [[5, 0, '#646170'], [9, 0.45, '#6c6876']], STEAM_UP = ['#7c777e', '#908a84'];   // stepped down with the night poster
function drawPoster(t, lv) {
  ctx.drawImage(POSTERS[0], BILL.x, BILL.y);
  if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(POSTERS[1], BILL.x, BILL.y); ctx.globalAlpha = 1; }
  const x0 = BILL.x + CUP.x, rim = BILL.y + CUP.y;          // two wisps of steam curling up from the cup
  for (const [ox, ph, col] of STEAM) for (let j = 1; j <= 7; j++) {
    const y = rim - j, sway = Math.sin(y * 1.1 + TAU * (72 * t / LOOP + ph)) * Math.min(1.7, j * 0.45);
    px(ctx, x0 + ox + Math.round(sway), y, j < 4 ? col : j < 6 ? STEAM_UP[0] : STEAM_UP[1]);
  }
}
function drawBillboard(t, pitch) {
  const lv = flick(t, BILL_FLICK, 13);
  halo(HL.billGlow, BILL.x + (BILL.w >> 1), BILL.y - 4, (0.9 + 0.1 * wave(t, 8, 0.2)) * (2 + lv) / 3);
  blit(BILL_C);
  if (!PITCH.covers(t, pitch)) drawPoster(t, lv);
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => {
    const a = i === 2 ? lv : 1;
    if (a < 1) rect(ctx, lx - 2, BILL_LAMP_Y + 2, 5, 1, `rgba(40,26,18,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.billLamp, lx, BILL_LAMP_Y + 3, a * (0.92 + 0.08 * wave(t, 700 + i * 37)));
  });
}
function drawSmoke(t) {
  for (const s of SMOKE) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = (s.pot ? 457 : 450) - u * u * 46 - u * 8 + wave(t, s.k, s.p) * 2 * u, y = 77 - u * 42 + u * u * 10;
    const rr = Math.min(6, 1 + Math.floor(u * 6));
    ctx.globalAlpha = 0.4 * Math.min(1, u * 7) * Math.pow(1 - u, 1.3);
    ctx.drawImage(PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}
function drawPigeons(t) {
  const wx = 262 + 16 * wave(t, 3, 0.1), vel = Math.cos(TAU * (3 * t / LOOP + 0.1));
  const moving = Math.abs(vel) > 0.3, f = vel < 0 ? 1 : 0;
  const k = moving ? (step(t, 1440) & 1 ? 'walk' : 'up') : hash(1, step(t, 480), 3) < 0.5 ? 'peck' : 'up';
  ctx.drawImage(PIG[k][f], Math.round(wx), 250);
  ctx.drawImage(PIG[hash(2, step(t, 600), 3) < 0.45 ? 'peck' : 'up'][1], 296, 254);
  ctx.drawImage(PIG[hash(3, step(t, 300), 3) < 0.3 ? 'peck' : 'up'][0], 232, EMB - 5);
}
/* people with umbrellas walking along the Embankment, one each way per loop */
const WALKERS = [{ a: 14, b: 42, dir: 1, y: 262, coat: '#8a7258', um: '#1e1d28' }, { a: 150, b: 180, dir: -1, y: 265, coat: '#3a3a52', um: '#8e1e2a' }];
function drawWalkers(t) {
  for (const wk of WALKERS) {
    if (t < wk.a || t >= wk.b) continue;
    const u = (t - wk.a) / (wk.b - wk.a), d = wk.dir, y = wk.y;
    const x = Math.round(d > 0 ? -14 + u * (W + 28) : W + 14 - u * (W + 28));
    const ph = (t - wk.a) * 1.8, s = Math.sin(ph * TAU), bob = Math.abs(s) < 0.35 ? 1 : 0, top = y - 29 - bob;
    const warm = Math.abs(x - LAMP.x) < 48 || x > PHONE.x - 20;
    const edge = warm ? '#b07a4a' : '#6a6078';
    const l = Math.round(s * 2.5);
    rect(ctx, x - 1 + l, y - 9, 2, 9, '#1c1822'); rect(ctx, x - l, y - 9, 2, 9, '#2a2430');
    px(ctx, x - 1 + l + (d > 0 ? 1 : -1), y - 1, '#0c0a10'); px(ctx, x - l + (d > 0 ? 1 : -1), y - 1, '#0c0a10');
    rect(ctx, x - 3, top + 7, 7, 13, wk.coat); rect(ctx, x - 4, top + 15, 9, 6, wk.coat);
    rect(ctx, d > 0 ? x + 3 : x - 3, top + 8, 1, 12, edge); rect(ctx, x - 3, top + 14, 7, 1, '#241c26');
    rect(ctx, x - 1, top + 2, 3, 4, '#c8987a'); rect(ctx, x - 1, top + 2, 3, 1, '#2a1c18'); px(ctx, x - d, top + 3, '#2a1c18');
    const ux = x + d * 2;
    rect(ctx, ux, top - 3, 1, 12, '#121118'); rect(ctx, x + d, top + 8, 2, 2, wk.coat);
    [[-4, 2], [-3, 5], [-2, 7], [-1, 8], [0, 8]].forEach(([dy, hw]) => rect(ctx, ux - hw, top + dy, hw * 2 + 1, 1, wk.um));
    rect(ctx, ux - 4, top - 3, 5, 1, edge); px(ctx, ux - 6, top - 2, edge);
    for (const k of [-8, -3, 3, 8]) px(ctx, ux + k, top + 1, '#121118');
    reflect(ctx, t, { top: y, rows: H - y, x: x - 10, w: 21, squash: 1.4, alpha: 0.28, k1: 40, k2: 90 });
  }
}
function drawForeground(t, rain) {
  blit(GROUND_C);
  if (rain) drawRings(PUDDLE_RINGS, t, 50, 340, PAVE + 5, H - 3, 3);
  blit(LAMP_C);
  const pf = flick(t, PHONE_FLICK, 12);
  blit(PHONE_C);
  if (pf < 1) rect(ctx, PHONE.x + 2, PHONE.y + 11, 11, 24, `rgba(40,10,14,${(0.75 * (1 - pf)).toFixed(3)})`);
  blit(PUB_C);
  drawFox(t);
  drawPubWindows(t);
  drawPatrons(t);
  drawBaskets(t);
  drawSign(t);
  drawSmoke(t);
  // wet reflections on the pavement
  reflect(ctx, t, { top: 258, rows: H - 258, x: PUB_X, w: W - PUB_X, squash: 2, alpha: 0.32, k1: 40, k2: 90 });
  reflect(ctx, t, { top: PHONE.base, rows: H - PHONE.base, x: PHONE.x - 1, w: PHONE.w + 2, squash: 1.6, alpha: 0.34, k1: 40, k2: 90 });
  streak(ctx, t, LAMP.x, 5, '#ffc878', 0.4, 21, 263, H);
  streak(ctx, t, PHONE.x + 7, 7, '#ff5a4a', 0.22, 22, PHONE.base, H);
  // light: pub glow, lamps inside, the phone box, the lamppost
  drawPigeons(t);
  drawWalkers(t);
  halo(HL.pub, 414, 222, 0.95); halo(HL.pub, 463, 222, 0.7);
  halo(HL.bulb, 406, 204, 1); halo(HL.bulb, 421, 204, 1); halo(HL.bulb, 467, 220, 0.9);
  halo(HL.phone, PHONE.x + 7, PHONE.y + 22, pf); halo(HL.phoneCore, PHONE.x + 7, PHONE.y + 24, pf);
  halo(HL.phoneCore, PHONE.x + 7, PHONE.y + 6, 0.6 * pf);
  const li = (0.95 + 0.05 * wave(t, 1500, 0.3)) * flick(t, LAMP_FLICK, 11);
  if (li < 0.9) rect(ctx, LAMP.x - 4, 135, 9, 14, `rgba(24,14,8,${(0.9 - li).toFixed(3)})`);
  halo(HL.lamp, LAMP.x, LAMP.y, li); halo(HL.lampCore, LAMP.x, LAMP.y, li);
  blit(BRANCH_C);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves it out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; clock: London's time (core `hands`)
function renderFrame(t, { pitch, billboard = true, weather, clock, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS, wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawFar(t);
  drawFog(FOGS[0], t);
  if (wx.leaves) for (const l of LEAVES[0]) drawLeaf(l, t);
  if (wx.rain) drawRain(RAIN[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawEye(t);
  if (billboard) drawBillboard(t, pitch);                     // on the South Bank: in front of the far city, behind the bridge
  drawParliament(t, clock);
  drawFlock(t);
  drawBridge(t);
  drawRiver(t, wx.rain);
  drawBoat(t);
  drawFog(FOGS[1], t);
  if (wx.leaves) for (const l of LEAVES[1]) drawLeaf(l, t);
  if (wx.rain) drawRain(RAIN[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawForeground(t, wx.rain);
  if (wx.rain) drawRain(RAIN[2], t, lights);
  if (wx.leaves) for (const l of LEAVES[2]) drawLeaf(l, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'london',
  name: 'London',
  tagline: 'Fog on the Thames',
  timeZone: 'Europe/London',
  accent: '#e8413c',
  focusX: BB.cx,
  landmark: [BB.cx - 10, BB.cx + 10],                  // Elizabeth Tower (Big Ben)
  landmarkTop: 34,                                     // the row of the spire's finial, which landscape crops keep
  billboard: BILL,
  perch: { dx: 2, dy: 5 },                             // the What's new pigeon (site/js/ui/pigeon.js): on the right-hand lamp
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'bell', x: BB.cx - 9, y: BB.clockY - 34, w: 19, h: 44 },                     // Big Ben: the belfry and the clock
    { kind: 'water', x: 60, y: WL + 1, w: 285, h: 8 },                                   // the Thames under Parliament and the bridge
    { kind: 'window', x: PUB_WINS[3].x, y: PUB_WINS[3].y, w: PUB_WINS[3].w, h: PUB_WINS[3].h },   // a room above the pub
  ],
  secrets: [                                           // hidden things a tap finds, each a passport sticker (site/js/ui/secrets.js)
    { id: 'fox', name: 'a fox on the pub roof', x: FOX.x - 1, y: FOX.y - 1, w: 12, h: 8, art: [
      '..o..o......', '..oooo......', '.wokoo......', 'wwooo.......', '.kwoooo..oo.', '..wwoooo.ooo',
      '..wwooooOoow', '..wooooOOoww', '..ooooOOoww.', '..oOoOOOO...', '..O.O.O.....', '............'] },
    { id: 'swan', name: 'a swan on the Thames', x: SWAN.x - 1, y: SWAN.y - 1, w: 9, h: 9, art: [
      '............', '..www.......', '.owkw.......', '..oww.......', '...ww.......', '...ww...ww..',
      '...www.wwwW.', '...wwwwwwwWW', '....wwwwwWW.', 'bbBbbbBbbbBb', '.b..Bb...b..', '............'] },
    { id: 'ghost', name: 'a ghost in the old tower', x: GHOST.x - 3, y: GHOST.y - 3, w: 11, h: 13, at: GHOST_UP, art: [
      '............', '....wwww....', '...wwwwww...', '..wwwwwwwW..', '..wkkwkkwW..', '..wkkwkkwW..',
      '..wwwwwwwW..', '..wwwkwwwW..', '..wwwwwwwW..', '..wwwwwwwW..', '..wwWwwWwW..', '..w.W.w.W...'] },
  ],
  ambience: { rain: 0.3, traffic: 0.3, water: 0.3, wind: 0.2, crowd: 0.15 },
  events: [...BONGS.map(t => ({ t, type: 'bell' })), { t: 124, type: 'horn' }],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
